import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Plus, Phone, MessageSquare, Calendar, Users, Wallet, User, Edit2, Trash2, Upload, X, Maximize2 } from "lucide-react";
import { clientApi, type Client, resolveUploadUrl, isPdfUrl, getAdminToken } from "@/services/api";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { dietPlanApi, DietPlan } from "@/services/api";
import { getLatestBmrUpdate, needsDietReview } from "@/lib/dietReview";
import mongoose from "mongoose";
import { workoutPlanApi, WorkoutPlan, clientWorkoutApi, exerciseApi, Exercise } from "@/services/api";

const healthNoteCategoryLabels: Record<string, string> = {
  'stomach': 'أمراض المعدة',
  'diabetes': 'السكري',
  'cholesterol': 'الكوليسترول',
  'ibs': 'القولون العصبي',
  'blood-pressure': 'ضغط الدم',
  'anemia': 'فقر الدم',
  'kidney': 'أمراض الكلى',
  'custom': 'ملاحظة مخصصة',
};

const healthNoteTemplates: Record<string, { title: string; content: string }> = {
  'stomach': {
    title: 'تعليمات غذائية لمرضى المعدة',
    content: `أولاً: تنظيم نمط الأكل
• تناول الطعام في وضع جلوس مستقيم
• تناول الطعام ببطء مع مضغ جيد
• تقسيم الوجبات إلى 4-6 وجبات صغيرة خلال اليوم
• الانتظار حوالي دقيقة بين كل لقمة والأخرى
• تجنب النوم بعد الأكل مباشرة (الانتظار لمدة لا تقل عن ساعتين)
• عند النوم، يفضل رفع مستوى الرأس باستخدام وسادة إضافية

أطعمة يُفضل تجنبها ❌
• الأطعمة الدسمة والمقلية
• الأكل الحار والتوابل القوية
• المشروبات الغازية (الصودا)
• الكافيين (القهوة - الشاي الثقيل)
• الأطعمة الحمضية (الليمون، البرتقال، الطماطم بكميات كبيرة)
• الأطعمة التي تسبب غازات وانتفاخ (البقوليات: فول - عدس - حمص، الكرنب - القرنبيط - البروكلي)
• الأطعمة التي تبطئ إفراغ المعدة (الأكل الدسم جداً، الوجبات الثقيلة الكبيرة، الحلويات الدسمة)

تقليل أطعمة الـ FODMAP ⚠️
• يفضل تقليل الأطعمة عالية الـ FODMAP لأنها قد تسبب انتفاخ وعدم راحة
• أمثلة: البصل والثوم، بعض الفواكه (مثل التفاح والكمثرى)، بعض منتجات الألبان

أطعمة يُنصح بها (سهلة الهضم) ✅
• الحبوب والنشويات: الأرز الأبيض، البطاطس المسلوقة أو المهروسة
• البروتينات الخفيفة: دجاج مسلوق أو مشوي، سمك مشوي
• الخضروات المطبوخة جيداً: كوسة، جزر، بطاطس
• الفواكه الخفيفة: موز، تفاح مطبوخ
• مشروبات مفيدة للمعدة: النعناع، الزنجبيل، البابونج (تساعد على تهدئة المعدة وتحسين الهضم)

البروبيوتك (Probiotics)
• يُنصح بتناول أطعمة تحتوي على بكتيريا نافعة (الزبادي، الزبادي اليوناني)
• تساهم في تحسين صحة الجهاز الهضمي وتقليل الالتهاب

نصائح إضافية مهمة
• شرب الماء بين الوجبات وليس أثناء الأكل بكميات كبيرة
• تقليل التوتر لأنه يؤثر على صحة المعدة
• طهي الطعام جيداً وتجنب الأكل النيء أو الثقيل على المعدة
• تجنب ارتداء الملابس الضيقة حول البطن

ملاحظات: استجابة الجسم تختلف من شخص لآخر، لذلك يفضل ملاحظة الأطعمة التي تسبب إزعاجاً وتجنبها. في حالة استمرار الأعراض يجب الرجوع إلى الطبيب المختص.`
  },
  'diabetes': {
    title: 'تعليمات غذائية لمرضى السكري',
    content: `تنظيم الوجبات
• تناول 3 وجبات رئيسية و2-3 وجبات خفيفة يومياً بمواعيد ثابتة
• عدم تخطي أي وجبة خاصة الفطور
• التحكم في حجم الحصص الغذائية

أطعمة يُفضل تجنبها أو تقليلها ❌
• السكريات والحلويات والعصائر المحلاة
• المشروبات الغازية والعصائر المعلبة
• الأرز الأبيض والخبز الأبيض بكميات كبيرة
• الأطعمة المقلية والدهون المشبعة
• الفواكه عالية السكر بكميات كبيرة (التمر، العنب، المانجو)

أطعمة يُنصح بها ✅
• الحبوب الكاملة: خبز أسمر، أرز بني، شوفان
• البروتينات: دجاج، سمك، بيض، بقوليات
• الخضروات الطازجة والمطبوخة بكثرة
• الفواكه قليلة السكر: تفاح، برتقال، توت
• الدهون الصحية: زيت زيتون، مكسرات (بكميات معتدلة)

نصائح مهمة
• شرب كمية كافية من الماء يومياً (8 أكواب على الأقل)
• ممارسة الرياضة بانتظام (المشي 30 دقيقة يومياً)
• مراقبة مستوى السكر في الدم بانتظام
• الاهتمام بقراءة الملصقات الغذائية على المنتجات
• الابتعاد عن التدخين والكحول`
  },
  'cholesterol': {
    title: 'تعليمات غذائية لمرضى الكوليسترول',
    content: `أطعمة يُفضل تجنبها ❌
• الدهون المشبعة: اللحوم الدهنية، جلد الدجاج، الزبدة، السمنة
• الأطعمة المقلية والوجبات السريعة
• منتجات الألبان كاملة الدسم
• صفار البيض بكميات كبيرة (يُفضل 3-4 بيضات أسبوعياً)
• اللحوم المصنعة: سجق، لانشون، هوت دوج
• الحلويات الدسمة والمعجنات

أطعمة يُنصح بها ✅
• الأسماك الدهنية (سلمون، تونة، ماكريل) - مرتين أسبوعياً
• زيت الزيتون بدلاً من الزبدة والسمنة
• الشوفان والحبوب الكاملة
• المكسرات: لوز، جوز (حفنة يومياً)
• الخضروات والفواكه الطازجة بكثرة
• البقوليات: فول، عدس، حمص
• منتجات الألبان قليلة أو خالية الدسم

نصائح مهمة
• ممارسة الرياضة بانتظام (30-45 دقيقة يومياً)
• الحفاظ على وزن صحي
• الإكثار من الألياف في الطعام
• تقليل الملح في الطعام
• الإقلاع عن التدخين`
  },
  'ibs': {
    title: 'تعليمات غذائية للقولون العصبي',
    content: `تنظيم نمط الأكل
• تناول وجبات صغيرة ومتكررة بدلاً من وجبات كبيرة
• تناول الطعام ببطء ومضغ الطعام جيداً
• شرب الماء بكميات كافية بين الوجبات
• تحديد مواعيد ثابتة للوجبات

أطعمة يُفضل تجنبها ❌
• الأطعمة عالية الـ FODMAP: البصل، الثوم، القمح بكميات كبيرة
• البقوليات: فول، عدس، حمص (أو تقليلها)
• منتجات الألبان (خاصة الحليب كامل الدسم)
• المشروبات الغازية والكافيين
• الأطعمة الحارة والتوابل القوية
• الأطعمة المقلية والدسمة
• بعض الفواكه: التفاح، الكمثرى، المانجو
• المحليات الصناعية (السوربيتول، المانيتول)

أطعمة يُنصح بها ✅
• الأرز الأبيض والبطاطس المسلوقة
• الموز والتوت والعنب
• الخضروات المطبوخة: كوسة، جزر، سبانخ
• البروتينات: دجاج، سمك، بيض
• الزنجبيل والنعناع (مهدئات طبيعية للقولون)
• الشوفان (بكميات معتدلة)

نصائح مهمة
• تقليل التوتر والضغط النفسي (يؤثر بشكل مباشر على القولون)
• ممارسة تمارين الاسترخاء والتنفس
• ممارسة الرياضة الخفيفة بانتظام
• تسجيل الأطعمة التي تسبب أعراضاً لتجنبها
• النوم الكافي (7-8 ساعات يومياً)`
  },
  'blood-pressure': {
    title: 'تعليمات غذائية لمرضى ضغط الدم',
    content: `أطعمة يُفضل تجنبها أو تقليلها ❌
• الملح والأطعمة المملحة (المخللات، الجبن المالح، الشيبس)
• اللحوم المصنعة والمعلبة
• الأطعمة المقلية والدهون المشبعة
• الكافيين بكميات كبيرة
• المشروبات الغازية والعصائر المحلاة
• الصلصات الجاهزة والكاتشب (عالية الصوديوم)

أطعمة يُنصح بها ✅
• الخضروات والفواكه الطازجة بكثرة (غنية بالبوتاسيوم)
• الموز، البرتقال، البطاطس الحلوة، السبانخ
• الأسماك الدهنية (سلمون، تونة)
• الحبوب الكاملة: شوفان، أرز بني
• منتجات الألبان قليلة الدسم
• المكسرات غير المملحة
• الثوم (يساعد في خفض الضغط)

نظام DASH الغذائي
• يُعتبر من أفضل الأنظمة لمرضى الضغط
• يركز على الفواكه والخضروات والحبوب الكاملة
• تقليل الصوديوم إلى أقل من 2300 ملغ يومياً

نصائح مهمة
• تقليل الملح تدريجياً واستخدام الأعشاب والتوابل كبديل
• الحفاظ على وزن صحي
• ممارسة الرياضة بانتظام (المشي 30 دقيقة يومياً)
• تقليل التوتر وممارسة الاسترخاء
• مراقبة ضغط الدم بانتظام
• الإقلاع عن التدخين`
  },
  'anemia': {
    title: 'تعليمات غذائية لمرضى فقر الدم',
    content: `أطعمة غنية بالحديد يُنصح بها ✅
• اللحوم الحمراء (أفضل مصدر للحديد)
• الكبدة (غنية جداً بالحديد وفيتامين B12)
• الدجاج والأسماك
• البيض
• البقوليات: عدس، فول، حمص، فاصوليا
• الخضروات الورقية الداكنة: سبانخ، ملوخية، جرجير
• الفواكه المجففة: زبيب، تمر، مشمش مجفف
• العسل الأسود

أطعمة تساعد على امتصاص الحديد
• فيتامين C: برتقال، ليمون، فراولة، فلفل ألوان
• يُفضل تناول مصدر فيتامين C مع الوجبات الغنية بالحديد

أطعمة تعيق امتصاص الحديد ❌ (تجنبها مع الوجبات)
• الشاي والقهوة (الانتظار ساعة على الأقل بعد الأكل)
• الحليب ومنتجات الألبان (الكالسيوم يعيق امتصاص الحديد)
• الأطعمة عالية الألياف بكميات كبيرة مع وجبات الحديد

نصائح مهمة
• طهي الطعام في أواني حديدية يزيد من محتوى الحديد
• تناول مكملات الحديد حسب وصف الطبيب (على معدة فارغة مع عصير برتقال)
• إجراء فحوصات دم دورية لمتابعة مستوى الحديد
• عدم تناول مكملات الحديد مع الحليب أو الشاي`
  },
  'kidney': {
    title: 'تعليمات غذائية لمرضى الكلى',
    content: `أطعمة يُفضل تقليلها ❌
• البروتينات بكميات كبيرة (اللحوم، الدجاج، الأسماك) - حسب توصية الطبيب
• الأطعمة عالية البوتاسيوم: الموز، البرتقال، البطاطس، الطماطم، السبانخ
• الأطعمة عالية الفوسفور: منتجات الألبان، المكسرات، المشروبات الغازية الداكنة
• الملح والأطعمة المملحة
• الأطعمة المعلبة والمصنعة

أطعمة يُنصح بها ✅
• الأرز الأبيض والمعكرونة
• الخبز الأبيض (أقل في الفوسفور من الأسمر)
• التفاح، التوت، العنب (فواكه قليلة البوتاسيوم)
• القرنبيط، الملفوف، الفلفل الأخضر
• البيض الأبيض (بروتين عالي الجودة قليل الفوسفور)

التحكم في السوائل
• قد يُطلب تقليل السوائل حسب حالة الكلى
• مراقبة كمية الماء والسوائل اليومية حسب توصية الطبيب

نصائح مهمة
• الالتزام بالكميات المحددة من البروتين يومياً
• قراءة الملصقات الغذائية للتحقق من الصوديوم والبوتاسيوم والفوسفور
• سلق الخضروات يقلل من محتواها من البوتاسيوم
• متابعة فحوصات الدم بانتظام
• الالتزام بتوصيات طبيب الكلى وأخصائي التغذية`
  },
};

const ClientDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("personal");
  const [showObservationDialog, setShowObservationDialog] = useState(false);
  const [showMeasurementsDialog, setShowMeasurementsDialog] = useState(false);
  const [showInBodyDialog, setShowInBodyDialog] = useState(false);
  const [showInBodyViewDialog, setShowInBodyViewDialog] = useState(false);
  const [showMedicalDialog, setShowMedicalDialog] = useState(false);
  const [showGalleryDialog, setShowGalleryDialog] = useState(false);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [showDietDialog, setShowDietDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showImageViewerDialog, setShowImageViewerDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [itemToDelete, setItemToDelete] = useState<{type: string, index: number} | null>(null);
  const [selectedInBody, setSelectedInBody] = useState<any>(null);
  const [isEditingInBody, setIsEditingInBody] = useState(false);
  const [isEditingObservation, setIsEditingObservation] = useState(false);
  const [isEditingMeasurement, setIsEditingMeasurement] = useState(false);
  const [isEditingMedical, setIsEditingMedical] = useState(false);
  const [isEditingBmr, setIsEditingBmr] = useState(false);
  const [selectedBmr, setSelectedBmr] = useState<any>(null);
  const [selectedObservation, setSelectedObservation] = useState<any>(null);
  const [selectedMeasurement, setSelectedMeasurement] = useState<any>(null);
  const [galleryImages, setGalleryImages] = useState<Array<{
    url: string;
    date: Date;
    notes?: string;
    createdAt: Date;
  }>>([]);
  const [galleryDate, setGalleryDate] = useState('');
  const [galleryNotes, setGalleryNotes] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [showHealthNoteDialog, setShowHealthNoteDialog] = useState(false);
  const [isEditingHealthNote, setIsEditingHealthNote] = useState(false);
  const [selectedHealthNote, setSelectedHealthNote] = useState<any>(null);
  const [healthNoteData, setHealthNoteData] = useState({
    title: '',
    content: '',
    categories: [] as string[]
  });

  const [observationData, setObservationData] = useState({
    date: '',
    notes: ''
  });

  const [measurementData, setMeasurementData] = useState({
    date: '',
    weight: '',
    bodyFat: '',
    chest: '',
    waist: '',
    hips: '',
    arms: '',
    thighs: ''
  });

  const [inBodyData, setInBodyData] = useState({
    date: '',
    weight: '',
    skeletalMuscle: '',
    bodyFat: '',
    bmi: '',
    bodyFatMass: '',
    pbf: '',
    minerals: '',
    protein: '',
    notes: '',
    fileUrl: ''
  });

  const [dietData, setDietData] = useState({
    date: '',
    name: '',
    type: '',
    goal: '',
    calories: '',
    protein: '',
    carbs: '',
    fats: '',
    notes: '',
    mealPlan: []
  });

  const [medicalData, setMedicalData] = useState({
    conditions: '',
    medications: '',
    allergies: '',
    bloodType: '',
    reassessmentDate: '',
    notes: ''
  });

  const [subscriptionData, setSubscriptionData] = useState({
    startDate: '',
    endDate: '',
    note: ''
  });

  const [editFormData, setEditFormData] = useState({
    name: "",
    email: "",
    phone: "",
    age: 0,
    gender: "",
    goal: "",
    profession: "",
    height: "",
    weight: "",
    dietType: "",
    allergies: "",
    foodPreferences: "",
    hasWhatsApp: false
  });

  const [longPressTimer, setLongPressTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [dietPlans, setDietPlans] = useState<DietPlan[]>([]);
  const [selectedDietPlan, setSelectedDietPlan] = useState<DietPlan | null>(null);
  const [showDietPlanDialog, setShowDietPlanDialog] = useState(false);
  const [debugExpr, setDebugExpr] = useState('');
  const [calcClicked, setCalcClicked] = useState(false);
  const [activity, setActivity] = useState('1.2');
  // Add state for protein percentage
  const [proteinPercent, setProteinPercent] = useState('');
  const [carbPercent, setCarbPercent] = useState('');
  const [fatPercent, setFatPercent] = useState('');
  const [fatBody, setFatBody] = useState(''); // For Katch-McArdle
  const [dietPlanName, setDietPlanName] = useState('');
  const [dietPlanDate, setDietPlanDate] = useState('');
  const [workoutPlans, setWorkoutPlans] = useState<WorkoutPlan[]>([]);
  const [showWorkoutPlanDialog, setShowWorkoutPlanDialog] = useState(false);
  const [selectedWorkoutPlan, setSelectedWorkoutPlan] = useState<WorkoutPlan | null>(null);
  const [removingWorkoutIndex, setRemovingWorkoutIndex] = useState<number | null>(null);
  const [showRemoveWorkoutConfirm, setShowRemoveWorkoutConfirm] = useState(false);
  const [exercises, setExercises] = useState<Exercise[]>([]);

  const handleImageLongPress = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setShowImageViewerDialog(true);
  };

  const handleImagePressStart = (imageUrl: string) => {
    const timer = setTimeout(() => {
      handleImageLongPress(imageUrl);
    }, 500); // 500ms for long press
    setLongPressTimer(timer);
  };

  const handleImagePressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  useEffect(() => {
    const fetchClient = async () => {
      try {
        setLoading(true);
        const response = await clientApi.getById(id || '');
        setClient(response);
      } catch (error) {
        console.error("Error fetching client:", error);
        toast({
          title: "Error",
          description: "Failed to load client details. Please try again.",
          variant: "destructive",
        });
        navigate("/clients");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchClient();
      // Fetch diet plans for this client
      dietPlanApi.getAll(id).then(setDietPlans).catch(() => setDietPlans([]));
      // Fetch workout plans for this client
      workoutPlanApi.getAll(id).then(setWorkoutPlans).catch(() => setWorkoutPlans([]));
    } else {
      navigate("/clients");
    }
  }, [id, navigate]);

  useEffect(() => {
    if (client) {
      setEditFormData({
        name: client.name || "",
        email: client.email || "",
        phone: client.phone || "",
        age: client.age || 0,
        gender: client.gender || "",
        goal: client.goal || "",
        profession: client.profession || "",
        height: client.height?.toString() || "",
        weight: client.weight?.toString() || "",
        dietType: client.dietType || "",
        allergies: client.allergies?.join(", ") || "",
        foodPreferences: client.foodPreferences || "",
        hasWhatsApp: client.hasWhatsApp || false
      });
    }
  }, [client]);

  useEffect(() => {
    if (client) {
      setGalleryImages(client.gallery || []);
    }
  }, [client]);

  useEffect(() => {
    if (activeTab === 'workout') {
      exerciseApi.getAll().then(setExercises).catch(() => setExercises([]));
    }
  }, [activeTab]);

  // When the Diet tab is opened for a client whose BMR review is overdue
  // (10+ days since last BMR add/edit), mark it reviewed so the red dot on the
  // Clients list is cleared. Only writes when a dot is actually showing.
  useEffect(() => {
    if (activeTab !== 'diet' || !client || !id) return;
    const latestUpdate = getLatestBmrUpdate(client.diet);
    if (!needsDietReview(latestUpdate, client.dietReviewedAt)) return;
    const now = new Date().toISOString();
    clientApi.update(id, { dietReviewedAt: now })
      .then(() => setClient(prev => (prev ? { ...prev, dietReviewedAt: now } : prev)))
      .catch(() => { /* non-critical: dot will retry on next open */ });
  }, [activeTab, client, id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-4rem)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  if (!client) return null;

  const handleAddObservation = () => {
    setShowObservationDialog(true);
  };

  const handleRecordMeasurements = () => {
    setShowMeasurementsDialog(true);
  };

  const handleUploadInBody = () => {
    setShowInBodyDialog(true);
  };

  const handleCreateDietPlan = () => {
    setShowDietDialog(true);
  };

  const handleEditBmr = (bmrPlan: any) => {
    setSelectedBmr(bmrPlan);
    setIsEditingBmr(true);
    setShowDietDialog(true);
  };

  const handleAddMedicalInfo = () => {
    setShowMedicalDialog(true);
  };

  const handleUploadPhotos = () => {
    setShowGalleryDialog(true);
  };

  const handleViewObservation = (observation: any) => {
    setSelectedObservation(observation);
    setShowObservationDialog(true);
    setObservationData({
      date: observation.date,
      notes: observation.notes
    });
    setIsEditingObservation(true);
  };

  const handleViewMeasurement = (measurement: any) => {
    setSelectedMeasurement(measurement);
    setShowMeasurementsDialog(true);
    setMeasurementData({
      date: measurement.date,
      weight: measurement.weight.toString(),
      bodyFat: measurement.bodyFat?.toString() || '',
      chest: measurement.chest?.toString() || '',
      waist: measurement.waist?.toString() || '',
      hips: measurement.hips?.toString() || '',
      arms: measurement.arms?.toString() || '',
      thighs: measurement.thighs?.toString() || ''
    });
    setIsEditingMeasurement(true);
  };

  const handleSaveObservation = async () => {
    try {
      if (!observationData.date || !observationData.notes) {
        toast({
          title: "Required Fields",
          description: "Please fill in both date and notes",
          variant: "destructive",
        });
        return;
      }

      const newObservation = {
        ...observationData,
        createdAt: new Date().toISOString()
      };

      if (isEditingObservation && selectedObservation) {
        // Update existing observation
        const updatedObservations = client?.observations?.map(item => 
          item.createdAt === selectedObservation.createdAt ? newObservation : item
        ) || [];
        
        await clientApi.update(id || '', {
          observations: updatedObservations
        });

        setClient(prev => prev ? {
          ...prev,
          observations: updatedObservations
        } : prev);
      } else {
        // Add new observation
        await clientApi.update(id || '', {
          observations: [...(client?.observations || []), newObservation]
        });

        setClient(prev => prev ? {
          ...prev,
          observations: [...(prev.observations || []), newObservation]
        } : prev);
      }

      setObservationData({
        date: '',
        notes: ''
      });
      setShowObservationDialog(false);
      setIsEditingObservation(false);
      setSelectedObservation(null);
      
      toast({
        title: isEditingObservation ? "Observation Updated" : "Observation Added",
        description: isEditingObservation ? "Observation has been updated successfully." : "New observation has been added successfully.",
      });
    } catch (error) {
      console.error("Error saving observation:", error);
      toast({
        title: "Error",
        description: "Failed to save observation. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAddHealthNote = () => {
    setHealthNoteData({ title: '', content: '', categories: [] });
    setIsEditingHealthNote(false);
    setSelectedHealthNote(null);
    setShowHealthNoteDialog(true);
  };

  const handleEditHealthNote = (note: any) => {
    setSelectedHealthNote(note);
    setHealthNoteData({
      title: note.title,
      content: note.content,
      categories: note.categories || []
    });
    setIsEditingHealthNote(true);
    setShowHealthNoteDialog(true);
  };

  const handleHealthNoteCategoryToggle = (category: string) => {
    setHealthNoteData(prev => {
      const isSelected = prev.categories.includes(category);
      let newCategories: string[];

      if (category === 'custom') {
        newCategories = isSelected
          ? prev.categories.filter(c => c !== 'custom')
          : [...prev.categories, 'custom'];
        return { ...prev, categories: newCategories };
      }

      if (isSelected) {
        newCategories = prev.categories.filter(c => c !== category);
      } else {
        newCategories = [...prev.categories, category];
      }

      const templateCategories = newCategories.filter(c => c !== 'custom' && healthNoteTemplates[c]);
      if (templateCategories.length > 0) {
        const combinedTitle = templateCategories.map(c => healthNoteTemplates[c].title).join(' + ');
        const combinedContent = templateCategories.map(c => healthNoteTemplates[c].content).join('\n\n━━━━━━━━━━━━━━━━━━━━\n\n');
        return { ...prev, categories: newCategories, title: combinedTitle, content: combinedContent };
      }

      return { ...prev, categories: newCategories, title: '', content: '' };
    });
  };

  const handleSaveHealthNote = async () => {
    try {
      if (!healthNoteData.title || !healthNoteData.content) {
        toast({
          title: "Required Fields",
          description: "Please fill in both title and content",
          variant: "destructive",
        });
        return;
      }

      const newNote = {
        ...healthNoteData,
        createdAt: isEditingHealthNote && selectedHealthNote ? selectedHealthNote.createdAt : new Date().toISOString()
      };

      if (isEditingHealthNote && selectedHealthNote) {
        const updatedNotes = client?.healthNotes?.map(item =>
          item.createdAt === selectedHealthNote.createdAt ? newNote : item
        ) || [];

        await clientApi.update(id || '', { healthNotes: updatedNotes });
        setClient(prev => prev ? { ...prev, healthNotes: updatedNotes } : prev);
      } else {
        await clientApi.update(id || '', {
          healthNotes: [...(client?.healthNotes || []), newNote]
        });
        setClient(prev => prev ? {
          ...prev,
          healthNotes: [...(prev.healthNotes || []), newNote]
        } : prev);
      }

      setHealthNoteData({ title: '', content: '', categories: [] });
      setShowHealthNoteDialog(false);
      setIsEditingHealthNote(false);
      setSelectedHealthNote(null);

      toast({
        title: isEditingHealthNote ? "Health Note Updated" : "Health Note Added",
        description: isEditingHealthNote ? "Health note has been updated successfully." : "New health note has been added successfully.",
      });
    } catch (error) {
      console.error("Error saving health note:", error);
      toast({
        title: "Error",
        description: "Failed to save health note. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSaveMeasurements = async () => {
    try {
      if (!measurementData.date || !measurementData.weight) {
        toast({
          title: "Required Fields",
          description: "Please fill in at least date and weight",
          variant: "destructive",
        });
        return;
      }

      const newMeasurement = {
        ...measurementData,
        weight: Number(measurementData.weight),
        bodyFat: measurementData.bodyFat ? Number(measurementData.bodyFat) : undefined,
        chest: measurementData.chest ? Number(measurementData.chest) : undefined,
        waist: measurementData.waist ? Number(measurementData.waist) : undefined,
        hips: measurementData.hips ? Number(measurementData.hips) : undefined,
        arms: measurementData.arms ? Number(measurementData.arms) : undefined,
        thighs: measurementData.thighs ? Number(measurementData.thighs) : undefined,
        createdAt: new Date().toISOString()
      };

      if (isEditingMeasurement && selectedMeasurement) {
        // Update existing measurement
        const updatedMeasurements = client?.measurements?.map(item => 
          item.createdAt === selectedMeasurement.createdAt ? newMeasurement : item
        ) || [];
        
        await clientApi.update(id || '', {
          measurements: updatedMeasurements,
          weight: Number(measurementData.weight)
        });

        setClient(prev => prev ? {
          ...prev,
          measurements: updatedMeasurements,
          weight: Number(measurementData.weight)
        } : prev);
      } else {
        // Add new measurement
        await clientApi.update(id || '', {
          measurements: [...(client?.measurements || []), newMeasurement],
          weight: Number(measurementData.weight)
        });

        setClient(prev => prev ? {
          ...prev,
          measurements: [...(prev.measurements || []), newMeasurement],
          weight: Number(measurementData.weight)
        } : prev);
      }

      setMeasurementData({
        date: '',
        weight: '',
        bodyFat: '',
        chest: '',
        waist: '',
        hips: '',
        arms: '',
        thighs: ''
      });
      setShowMeasurementsDialog(false);
      setIsEditingMeasurement(false);
      setSelectedMeasurement(null);
      
      toast({
        title: isEditingMeasurement ? "Measurements Updated" : "Measurements Recorded",
        description: isEditingMeasurement ? "Measurements have been updated successfully." : "New measurements have been recorded successfully.",
      });
    } catch (error) {
      console.error("Error saving measurements:", error);
      toast({
        title: "Error",
        description: "Failed to save measurements. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleViewInBody = (inbody: any) => {
    setSelectedInBody(inbody);
    setShowInBodyViewDialog(true);
  };

  const handleEditInBody = (inbody: any) => {
    setSelectedInBody(inbody);
    setInBodyData({
      date: inbody.date,
      weight: inbody.weight.toString(),
      skeletalMuscle: inbody.skeletalMuscle?.toString() || '',
      bodyFat: inbody.bodyFat.toString(),
      bmi: inbody.bmi?.toString() || '',
      bodyFatMass: inbody.bodyFatMass?.toString() || '',
      pbf: inbody.pbf?.toString() || '',
      minerals: inbody.minerals?.toString() || '',
      protein: inbody.protein?.toString() || '',
      notes: inbody.notes || '',
      fileUrl: inbody.fileUrl || ''
    });
    setIsEditingInBody(true);
    setShowInBodyDialog(true);
  };

  const handleSaveInBody = async () => {
    try {
      if (!inBodyData.date || !inBodyData.weight || !inBodyData.bodyFat || !inBodyData.fileUrl) {
        toast({
          title: "Required Fields",
          description: "Please fill in date, weight, body fat percentage, and upload a report file",
          variant: "destructive",
        });
        return;
      }

      const newInBody = {
        ...inBodyData,
        fileUrl: resolveUploadUrl(inBodyData.fileUrl),
        weight: Number(inBodyData.weight),
        skeletalMuscle: Number(inBodyData.skeletalMuscle),
        bodyFat: Number(inBodyData.bodyFat),
        bmi: Number(inBodyData.bmi),
        bodyFatMass: Number(inBodyData.bodyFatMass),
        pbf: Number(inBodyData.pbf),
        minerals: Number(inBodyData.minerals),
        protein: Number(inBodyData.protein),
        createdAt: new Date().toISOString()
      };

      if (isEditingInBody && selectedInBody) {
        // Update existing InBody entry
        const updatedInBody = client?.inBody?.map(item => 
          item.createdAt === selectedInBody.createdAt ? newInBody : item
        ) || [];
        
        await clientApi.update(id || '', {
          inBody: updatedInBody,
          weight: Number(inBodyData.weight)
        });

        setClient(prev => prev ? {
          ...prev,
          inBody: updatedInBody,
          weight: Number(inBodyData.weight)
        } : prev);
      } else {
        // Add new InBody entry
        await clientApi.update(id || '', {
          inBody: [...(client?.inBody || []), newInBody],
          weight: Number(inBodyData.weight)
        });

        setClient(prev => prev ? {
          ...prev,
          inBody: [...(prev.inBody || []), newInBody],
          weight: Number(inBodyData.weight)
        } : prev);
      }

      setInBodyData({
        date: '',
        weight: '',
        skeletalMuscle: '',
        bodyFat: '',
        bmi: '',
        bodyFatMass: '',
        pbf: '',
        minerals: '',
        protein: '',
        notes: '',
        fileUrl: ''
      });
      setShowInBodyDialog(false);
      setIsEditingInBody(false);
      setSelectedInBody(null);
      
      toast({
        title: isEditingInBody ? "InBody Results Updated" : "InBody Results Added",
        description: isEditingInBody ? "InBody results have been updated successfully." : "New InBody results have been added successfully.",
      });
    } catch (error) {
      console.error("Error saving InBody results:", error);
      toast({
        title: "Error",
        description: "Failed to save InBody results. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSaveDietPlan = async (dietPlanData: any) => {
    try {
      if (!dietPlanData.date || !dietPlanData.name || !dietPlanData.calories) {
        toast({
          title: "Required Fields",
          description: "Please fill in at least date, plan name, and calories",
          variant: "destructive",
        });
        return;
      }

      // Create diet data for client schema - matching the exact backend interface
      const newDiet: {
        date: string;
        name: string;
        type: string;
        weight: number;
        height: number;
        age: number;
        gender: string;
        equationMethod: string;
        bodyFat?: number;
        factor1?: number;
        factor2?: number;
        factor3?: number;
        factor4?: number;
        bmr: number;
        activityLevel: number;
        calories: number;
        protein?: number;
        carbs?: number;
        fats?: number;
        notes?: string;
        createdAt: string;
        updatedAt: string;
      } = {
        date: dietPlanData.date,
        name: dietPlanData.name,
        type: dietPlanData.type,
        weight: Number(dietPlanData.weight),
        height: Number(dietPlanData.height),
        age: Number(dietPlanData.age),
        gender: dietPlanData.gender,
        equationMethod: dietPlanData.equationMethod,
        bodyFat: Number(dietPlanData.bodyFat) || undefined,
        factor1: Number(dietPlanData.factor1) || undefined,
        factor2: Number(dietPlanData.factor2) || undefined,
        factor3: Number(dietPlanData.factor3) || undefined,
        factor4: Number(dietPlanData.factor4) || undefined,
        bmr: Number(dietPlanData.bmr),
        activityLevel: Number(dietPlanData.activityLevel),
        calories: Number(dietPlanData.calories),
        protein: Number(dietPlanData.protein) || undefined,
        carbs: Number(dietPlanData.carbs) || undefined,
        fats: Number(dietPlanData.fats) || undefined,
        notes: dietPlanData.notes,
        createdAt: isEditingBmr && selectedBmr ? selectedBmr.createdAt : new Date().toISOString(),
        // Always stamp the edit time so the "needs review" dot resets on every
        // BMR add/edit and re-appears 10 days later.
        updatedAt: new Date().toISOString()
      };

      let updatedClient;
      if (isEditingBmr && selectedBmr) {
        // Update existing BMR plan
        const updatedDiet = client?.diet?.map(diet => 
          diet.createdAt === selectedBmr.createdAt ? newDiet : diet
        ) || [];
        
        updatedClient = await clientApi.update(id || '', {
          diet: updatedDiet
        });
        
        toast({
          title: "Diet Plan Updated",
          description: "BMR diet plan has been updated successfully.",
        });
      } else {
        // Create new BMR plan
        updatedClient = await clientApi.update(id || '', {
          diet: [...(client?.diet || []), newDiet]
        });
        
        toast({
          title: "Diet Plan Created",
          description: "New diet plan has been created successfully.",
        });
      }

      // Update local state
      setClient(updatedClient);

      setShowDietDialog(false);
      setIsEditingBmr(false);
      setSelectedBmr(null);
      
    } catch (error) {
      console.error("Error saving diet plan:", error);
      toast({
        title: "Error",
        description: "Failed to save diet plan. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSaveMedicalInfo = async () => {
    try {
      const newMedicalInfo = {
        ...medicalData,
        conditions: medicalData.conditions.split(',').map(c => c.trim()).filter(Boolean),
        medications: medicalData.medications.split(',').map(m => m.trim()).filter(Boolean),
        allergies: medicalData.allergies.split(',').map(a => a.trim()).filter(Boolean),
        reassessmentDate: medicalData.reassessmentDate || undefined,
        lastUpdated: new Date().toISOString()
      };

      await clientApi.update(id || '', {
        medicalInfo: newMedicalInfo
      });

      setClient(prev => prev ? {
        ...prev,
        medicalInfo: newMedicalInfo
      } : prev);

      setMedicalData({
        conditions: '',
        medications: '',
        allergies: '',
        bloodType: '',
        reassessmentDate: '',
        notes: ''
      });
      setShowMedicalDialog(false);
      setIsEditingMedical(false);
      
      toast({
        title: "Medical Info Updated",
        description: "Medical information has been updated successfully.",
      });
    } catch (error) {
      console.error("Error saving medical info:", error);
      toast({
        title: "Error",
        description: "Failed to save medical information. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(files);
      
      // Create preview URLs
      const urls = files.map(file => URL.createObjectURL(file));
      setPreviewUrls(urls);
    }
  };

  const handleSaveGallery = async () => {
    if (!galleryDate) {
      toast({
        title: "Required Fields",
        description: "Please select a date for the photos",
        variant: "destructive",
      });
      return;
    }

    if (selectedFiles.length === 0) {
      toast({
        title: "Required Fields",
        description: "Please select at least one photo",
        variant: "destructive",
      });
      return;
    }

    try {
      const now = new Date();
      const uploadedImages = [];

      // Upload each file
      for (const file of selectedFiles) {
        console.log('Uploading file:', file.name);
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('/api/upload', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Upload failed:', errorData);
          throw new Error(errorData.message || 'Failed to upload file');
        }

        const data = await response.json();
        console.log('Upload successful:', data);
        
        uploadedImages.push({
          url: data.url,
          date: new Date(galleryDate),
          notes: galleryNotes,
          createdAt: now
        });
      }

      console.log('All files uploaded, updating gallery...');
      
      // Update the client's gallery in the database
      const updatedGallery = [...galleryImages, ...uploadedImages];
      const updateResponse = await clientApi.update(id || '', {
        ...client,
        gallery: updatedGallery
      });

      console.log('Gallery updated:', updateResponse);

      setGalleryImages(updatedGallery);
      setClient(prev => prev ? {
        ...prev,
        gallery: updatedGallery
      } : prev);

      // Reset form
      setGalleryDate('');
      setGalleryNotes('');
      setSelectedFiles([]);
      setPreviewUrls([]);
      setShowGalleryDialog(false);

      toast({
        title: "Images Uploaded",
        description: "Images have been uploaded successfully.",
      });
    } catch (error) {
      console.error("Error uploading images:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload images. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteGalleryItem = async (index: number) => {
    try {
      const updatedGallery = galleryImages.filter((_, i) => i !== index);
      await clientApi.update(id || '', {
        ...client,
        gallery: updatedGallery
      });

      setGalleryImages(updatedGallery);
      setClient(prev => prev ? {
        ...prev,
        gallery: updatedGallery
      } : prev);

      toast({
        title: "Image Deleted",
        description: "The image has been deleted successfully.",
      });
    } catch (error) {
      console.error("Error deleting image:", error);
      toast({
        title: "Error",
        description: "Failed to delete the image. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleConfirmDelete = (type: string, index: number) => {
    setItemToDelete({ type, index });
    setShowDeleteConfirmDialog(true);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete || !client) return;

    try {
      let updatedClient = { ...client };
      
      switch (itemToDelete.type) {
        case 'healthNote':
          updatedClient.healthNotes = client.healthNotes?.filter((_, index) => index !== itemToDelete.index);
          break;
        case 'observation':
          updatedClient.observations = client.observations?.filter((_, index) => index !== itemToDelete.index);
          break;
        case 'measurement':
          updatedClient.measurements = client.measurements?.filter((_, index) => index !== itemToDelete.index);
          break;
        case 'inbody':
          updatedClient.inBody = client.inBody?.filter((_, index) => index !== itemToDelete.index);
          break;
        case 'diet':
          updatedClient.diet = client.diet?.filter((_, index) => index !== itemToDelete.index);
          break;
        case 'meal-diet':
          // Delete from dietPlans array and API
          const planToDelete = dietPlans[itemToDelete.index];
          if (planToDelete?._id) {
            await dietPlanApi.delete(planToDelete._id);
            setDietPlans(plans => plans.filter((_, i) => i !== itemToDelete.index));
          }
          break;
        case 'gallery':
          const updatedGallery = galleryImages.filter((_, index) => index !== itemToDelete.index);
          updatedClient.gallery = updatedGallery;
          setGalleryImages(updatedGallery);
          break;
        case 'medical':
          updatedClient.medicalInfo = undefined;
          // Also remove from the client state immediately
          setClient(prev => prev ? { ...prev, medicalInfo: undefined } : prev);
          break;
        case 'subscription':
          updatedClient.subscription = undefined;
          setClient(prev => prev ? { ...prev, subscription: undefined } : prev);
          break;
      }

      // For medical info / subscription deletion, we need to explicitly send
      // null so the backend clears the field.
      if (itemToDelete.type === 'medical') {
        await clientApi.update(id || '', { medicalInfo: null });
      } else if (itemToDelete.type === 'subscription') {
        await clientApi.update(id || '', { subscription: null as any });
      } else {
        await clientApi.update(id || '', updatedClient);
      }
      setClient(updatedClient);
      
      toast({
        title: "Item Deleted",
        description: "The item has been deleted successfully.",
      });
    } catch (error) {
      console.error("Error deleting item:", error);
      toast({
        title: "Error",
        description: "Failed to delete the item. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowDeleteConfirmDialog(false);
      setItemToDelete(null);
    }
  };

  const handleEditClient = async () => {
    try {
      if (!editFormData.name || !editFormData.email || !editFormData.gender) {
        toast({
          title: "Required Fields",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return;
      }

      const updatedData = {
        ...editFormData,
        age: Number(editFormData.age),
        height: editFormData.height ? Number(editFormData.height) : undefined,
        weight: editFormData.weight ? Number(editFormData.weight) : undefined,
        allergies: editFormData.allergies ? editFormData.allergies.split(",").map(s => s.trim()) : undefined,
        gender: editFormData.gender as "male" | "female" | "other"
      };

      const updatedClient = await clientApi.update(id || "", updatedData);
      setClient(updatedClient);
      setShowEditDialog(false);
      
      toast({
        title: "Client Updated",
        description: "Client information has been updated successfully.",
        variant: "default",
      });
    } catch (error) {
      console.error("Error updating client:", error);
      toast({
        title: "Error",
        description: "Failed to update client. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [id]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEditSelectChange = (value: string) => {
    setEditFormData(prev => ({
      ...prev,
      gender: value
    }));
  };

  const handleInBodyInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setInBodyData(prev => ({
      ...prev,
      [id === 'inbody-date' ? 'date' :
       id === 'inbody-weight' ? 'weight' :
       id === 'inbody-muscle' ? 'skeletalMuscle' :
       id === 'inbody-bodyfat' ? 'bodyFat' :
       id === 'inbody-bmi' ? 'bmi' :
       id === 'inbody-fatmass' ? 'bodyFatMass' :
       id === 'inbody-minerals' ? 'minerals' :
       id === 'inbody-protein' ? 'protein' :
       id === 'inbody-notes' ? 'notes' : 'fileUrl']: value
    }));
  };

  const handleInBodyFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      try {
        const file = e.target.files[0];
        console.log('Uploading InBody file:', file.name);
        
        const formData = new FormData();
        formData.append('file', file);

        const token = getAdminToken();
        const response = await fetch('/api/upload', {
          method: 'POST',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Upload failed:', errorData);
          throw new Error(errorData.message || 'Failed to upload file');
        }

        const data = await response.json();
        console.log('InBody file upload successful:', data);
        
        setInBodyData(prev => ({
          ...prev,
          fileUrl: resolveUploadUrl(data.url)
        }));
        
        toast({
          title: "File Uploaded",
          description: "InBody report file has been uploaded successfully.",
        });
      } catch (error) {
        console.error('Error uploading InBody file:', error);
        toast({
          title: "Upload Failed",
          description: error.message || "Failed to upload InBody file. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const handleDietInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setDietData(prev => ({
      ...prev,
      [id === 'diet-date' ? 'date' :
       id === 'diet-name' ? 'name' :
       id === 'diet-calories' ? 'calories' :
       id === 'diet-protein' ? 'protein' :
       id === 'diet-carbs' ? 'carbs' :
       id === 'diet-fats' ? 'fats' :
       'notes']: value
    }));
  };

  const handleDietTypeChange = (value: string) => {
    setDietData(prev => ({
      ...prev,
      type: value
    }));
  };

  const handleDietGoalChange = (value: string) => {
    setDietData(prev => ({
      ...prev,
      goal: value
    }));
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(resolveUploadUrl(imageUrl));
    setShowImageViewerDialog(true);
  };

  const openInBodyFile = (fileUrl: string) => {
    const resolved = resolveUploadUrl(fileUrl);
    if (!resolved) return;
    if (isPdfUrl(fileUrl) || isPdfUrl(resolved)) {
      window.open(resolved, '_blank', 'noopener,noreferrer');
      return;
    }
    handleImageClick(resolved);
  };

  const handleViewWorkoutPlan = (plan: WorkoutPlan) => {
    setSelectedWorkoutPlan(plan);
    setShowWorkoutPlanDialog(true);
  };

  const handleRemoveWorkout = async (plan: WorkoutPlan, index: number) => {
    if (!client?._id || !plan._id) return;
    setRemovingWorkoutIndex(index);
    setShowRemoveWorkoutConfirm(true);
  };

  const confirmRemoveWorkout = async () => {
    if (removingWorkoutIndex === null || !client?._id || !workoutPlans[removingWorkoutIndex]?._id) return;
    try {
      await clientWorkoutApi.removeWorkout(client._id, workoutPlans[removingWorkoutIndex]._id!);
      setWorkoutPlans(plans => plans.filter((_, i) => i !== removingWorkoutIndex));
      toast({ title: "Workout Plan Removed", description: "The workout plan has been removed from this client." });
    } catch (error) {
      toast({ title: "Error", description: "Failed to remove workout plan.", variant: "destructive" });
    } finally {
      setShowRemoveWorkoutConfirm(false);
      setRemovingWorkoutIndex(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => navigate("/clients")}
            className="transition-all hover:scale-105 active:scale-95"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full overflow-hidden">
              <img 
                src={client.image} 
                alt={client.name} 
                className="h-full w-full object-cover" 
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{client.name}</h1>
              <p className="text-muted-foreground">{client.email}</p>
            </div>
          </div>
        </div>
        <Button 
          variant="outline"
          onClick={() => setShowEditDialog(true)}
          className="transition-all hover:scale-105 active:scale-95"
        >
          <Edit2 className="h-4 w-4 mr-2" />
          Edit Client
        </Button>
      </div>

      <Tabs 
        defaultValue="personal" 
        value={activeTab} 
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="grid grid-cols-3 sm:grid-cols-8 h-auto">
          <TabsTrigger value="personal">Personal</TabsTrigger>
          <TabsTrigger value="observation">Observations</TabsTrigger>
          <TabsTrigger value="measurements">Measurements</TabsTrigger>
          <TabsTrigger value="inbody">InBody</TabsTrigger>
          <TabsTrigger value="diet">Diet</TabsTrigger>
          <TabsTrigger value="workout">Workout</TabsTrigger>
          <TabsTrigger value="medical">Medical</TabsTrigger>
          <TabsTrigger value="gallery">Gallery</TabsTrigger>
          <TabsTrigger value="subscription">Subscription</TabsTrigger>
        </TabsList>
        
        <TabsContent value="personal" className="mt-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Email</h3>
                <p>{client.email}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Phone</h3>
                <div className="flex items-center gap-2">
                  <p>{client.phone}</p>
                  {client.hasWhatsApp ? (
                    <Badge variant="outline" className="flex items-center gap-1 border-green-500 text-green-600">
                      <MessageSquare className="h-3 w-3" />
                      WhatsApp
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      Calls only
                    </Badge>
                  )}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Age</h3>
                <p>{client.age}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Gender</h3>
                <p className="capitalize">{client.gender}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Profession</h3>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-500" />
                  <p>{client.profession || 'Not specified'}</p>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Visit Tracking</h3>
                <div className="flex flex-col gap-1 mt-1">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">Total: {client.visitCount?.total || 0} visits</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">This month: {client.visitCount?.thisMonth || 0} visits</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">This week: {client.visitCount?.thisWeek || 0} visits</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Height</h3>
                <p>{client.height ? `${client.height} cm` : 'Not recorded'}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Weight</h3>
                <p>{client.weight ? `${client.weight} kg` : 'Not recorded'}</p>
              </div>
              <div className="sm:col-span-2">
                <h3 className="text-sm font-medium text-gray-500">Outstanding Balance</h3>
                <div className="flex items-center gap-2 mt-1">
                  <Wallet className="h-4 w-4 text-gray-500" />
                  {client.outstandingBalance ? (
                    <p className="text-red-500 font-medium">${client.outstandingBalance} due</p>
                  ) : (
                    <p className="text-green-500 font-medium">No outstanding balance</p>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <h3 className="text-sm font-medium text-gray-500">Goal</h3>
                <p>{client.goal}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Diet Type</h3>
                <p>{client.dietType || 'Not specified'}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Allergies</h3>
                <p>{client.allergies && client.allergies.length > 0 ? 
                  client.allergies.join(', ') : 'None'}</p>
              </div>
              <div className="sm:col-span-2">
                <h3 className="text-sm font-medium text-gray-500">Food Preferences</h3>
                <p>{client.foodPreferences || 'None specified'}</p>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="subscription" className="mt-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-indigo-700">Subscription</h3>
              <div className="flex gap-2">
                <Button onClick={() => {
                  setSubscriptionData({
                    startDate: client.subscription?.startDate ? new Date(client.subscription.startDate).toISOString().slice(0,10) : '',
                    endDate: client.subscription?.endDate ? new Date(client.subscription.endDate).toISOString().slice(0,10) : '',
                    note: client.subscription?.note || ''
                  });
                  setShowSubscriptionDialog(true);
                }}>Edit</Button>
                {client.subscription?.startDate || client.subscription?.endDate || client.subscription?.note ? (
                  <Button
                    variant="destructive"
                    onClick={() => handleConfirmDelete('subscription', 0)}
                  >
                    Delete
                  </Button>
                ) : null}
              </div>
            </div>
            {client.subscription?.startDate || client.subscription?.endDate ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <div className="text-sm text-gray-500">Start Date</div>
                  <div className="font-medium">{client.subscription?.startDate ? new Date(client.subscription.startDate).toLocaleDateString() : '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">End Date</div>
                  <div className="font-medium">{client.subscription?.endDate ? new Date(client.subscription.endDate).toLocaleDateString() : '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Days Remaining</div>
                  <div className="font-medium">
                    {client.subscription?.endDate ? Math.max(0, Math.ceil((new Date(client.subscription.endDate as any).getTime() - Date.now()) / (1000*60*60*24))) : '-'}
                  </div>
                </div>
                {client.subscription?.note && (
                  <div className="sm:col-span-3">
                    <div className="text-sm text-gray-500">Note</div>
                    <div className="text-sm text-gray-700">{client.subscription.note}</div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground italic">No subscription set.</p>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="observation" className="mt-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-4">
              {client.observations && client.observations.length > 0 ? (
                <div className="space-y-4">
                  {client.observations.map((observation, index) => (
                    <div key={index} className="border-b pb-4">
                                              <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm text-gray-500">{new Date(observation.date).toLocaleDateString()}</p>
                            <p className="text-sm mt-1">{observation.notes}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewObservation(observation)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleConfirmDelete('observation', index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground italic">No observations recorded yet.</p>
              )}
              <Button onClick={handleAddObservation} className="transition-all hover:scale-105 active:scale-95">
                <Plus className="mr-2 h-4 w-4" /> Add Observation
              </Button>
            </div>
          </div>

          {/* Health Notes Section */}
          <div className="bg-white rounded-lg shadow p-6 mt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Health Notes</h3>
              <Button onClick={handleAddHealthNote} size="sm" className="transition-all hover:scale-105 active:scale-95">
                <Plus className="mr-2 h-4 w-4" /> Add Note
              </Button>
            </div>
            <div className="space-y-4">
              {client.healthNotes && client.healthNotes.length > 0 ? (
                <div className="space-y-3">
                  {client.healthNotes.map((note, index) => (
                    <div key={index} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h4 className="font-medium text-sm">{note.title}</h4>
                            {(note.categories || []).map((cat: string) => (
                              <Badge key={cat} variant="secondary" className="text-xs">
                                {healthNoteCategoryLabels[cat] || cat}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap line-clamp-3">{note.content}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(note.createdAt).toLocaleDateString()} at {new Date(note.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <div className="flex gap-2 ml-4 shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditHealthNote(note)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleConfirmDelete('healthNote', index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground italic">No health notes recorded yet.</p>
              )}
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="measurements" className="mt-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-4">
              {client.measurements && client.measurements.length > 0 ? (
                <div className="space-y-4">
                  {client.measurements.map((measurement, index) => (
                    <div key={index} className="border-b pb-4">
                                              <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm text-gray-500">{new Date(measurement.date).toLocaleDateString()}</p>
                            <p className="text-lg font-medium mt-1">Weight: {measurement.weight} kg</p>
                            {measurement.bodyFat && (
                              <p className="text-sm mt-1">Body Fat: {measurement.bodyFat}%</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewMeasurement(measurement)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleConfirmDelete('measurement', index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground italic">No measurements recorded yet.</p>
              )}
              <Button onClick={handleRecordMeasurements} className="transition-all hover:scale-105 active:scale-95">
                <Plus className="mr-2 h-4 w-4" /> Record Measurements
              </Button>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="inbody" className="mt-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-4">
              {client.inBody && client.inBody.length > 0 ? (
                <div className="space-y-4">
                  {client.inBody.map((inbody, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm text-gray-500">{new Date(inbody.date).toLocaleDateString()}</p>
                          <div className="grid grid-cols-2 gap-4 mt-2">
                            <div>
                              <p className="text-sm font-medium">Weight: {inbody.weight} kg</p>
                              <p className="text-sm">Body Fat: {inbody.bodyFat}%</p>
                              <p className="text-sm">BMI: {inbody.bmi}</p>
                            </div>
                            <div>
                              <p className="text-sm">Skeletal Muscle: {inbody.skeletalMuscle} kg</p>
                              <p className="text-sm">Body Fat Mass: {inbody.bodyFatMass} kg</p>
                              <p className="text-sm">Minerals: {inbody.minerals} kg</p>
                            </div>
                          </div>
                          {inbody.notes && (
                            <p className="text-sm mt-2 text-gray-600">Notes: {inbody.notes}</p>
                          )}
                          {inbody.fileUrl && (
                            <div className="mt-3 space-y-2">
                              <p className="text-sm font-medium text-blue-600">Report File:</p>
                              {!(isPdfUrl(inbody.fileUrl)) && (
                                <img
                                  src={resolveUploadUrl(inbody.fileUrl)}
                                  alt="InBody report"
                                  className="max-h-48 rounded-md border cursor-pointer hover:opacity-90"
                                  onClick={() => openInBodyFile(inbody.fileUrl!)}
                                />
                              )}
                              <button
                                type="button"
                                onClick={() => openInBodyFile(inbody.fileUrl!)}
                                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                              >
                                {isPdfUrl(inbody.fileUrl) ? '📄 Open PDF Report' : '🖼️ Open InBody Image'}
                              </button>
                            </div>
                          )}
                          <div className="flex gap-2 mt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewInBody(inbody)}
                            >
                              View Details
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditInBody(inbody)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleConfirmDelete('inbody', index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground italic">No InBody results uploaded yet.</p>
              )}
              <Button onClick={handleUploadInBody} className="transition-all hover:scale-105 active:scale-95">
                <Plus className="mr-2 h-4 w-4" /> Upload InBody Results
              </Button>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="diet" className="mt-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-6">
              {/* Last BMR update banner */}
              {(() => {
                const lastBmrUpdate = getLatestBmrUpdate(client?.diet);
                return (
                  <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 border rounded-lg px-4 py-2">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-gray-700">BMR last updated:</span>
                    <span>
                      {lastBmrUpdate
                        ? lastBmrUpdate.toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                        : "No BMR plan yet"}
                    </span>
                  </div>
                );
              })()}

              {/* BMR Diet Plans Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4 text-blue-600">BMR Diet Plans</h3>
                {client?.diet && client.diet.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {client.diet.map((dietPlan, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-gradient-to-br from-blue-50 to-white shadow hover:shadow-md transition">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold text-lg text-blue-800">{dietPlan.name}</h4>
                            <p className="text-sm text-gray-600">{dietPlan.type}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditBmr(dietPlan)}
                              className="text-blue-500 hover:text-blue-700"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleConfirmDelete('diet', index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Date:</span>
                            <span className="font-medium">{new Date(dietPlan.date).toLocaleDateString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">BMR:</span>
                            <span className="font-medium text-blue-600">{dietPlan.bmr} cal/day</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Calories:</span>
                            <span className="font-medium text-green-600">{dietPlan.calories} cal/day</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Activity Level:</span>
                            <span className="font-medium">{dietPlan.activityLevel}</span>
                          </div>
                        </div>

                        {/* Macronutrients */}
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs font-medium text-gray-700 mb-2">Macronutrients:</p>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div className="text-center">
                              <div className="font-bold text-purple-600">{dietPlan.protein}g</div>
                              <div className="text-gray-500">Protein</div>
                            </div>
                            <div className="text-center">
                              <div className="font-bold text-yellow-600">{dietPlan.carbs}g</div>
                              <div className="text-gray-500">Carbs</div>
                            </div>
                            <div className="text-center">
                              <div className="font-bold text-pink-600">{dietPlan.fats}g</div>
                              <div className="text-gray-500">Fat</div>
                            </div>
                          </div>
                        </div>

                        {/* BMR Calculation Details */}
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs font-medium text-gray-700 mb-2">Calculation Details:</p>
                          <div className="space-y-1 text-xs text-gray-600">
                            <div>Equation: {dietPlan.equationMethod}</div>
                            <div>Weight: {dietPlan.weight}kg, Height: {dietPlan.height}cm</div>
                            <div>Age: {dietPlan.age}, Gender: {dietPlan.gender}</div>
                            {dietPlan.bodyFat && <div>Body Fat: {dietPlan.bodyFat}%</div>}
                          </div>
                        </div>

                        {dietPlan.notes && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-xs font-medium text-gray-700 mb-1">Notes:</p>
                            <p className="text-xs text-gray-600">{dietPlan.notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">No BMR diet plans created yet.</p>
                )}
              </div>

              {/* Meal-Based Diet Plans Section */}
              <div className="border-t pt-6"
              >
                <h3 className="text-lg font-semibold mb-4 text-green-600">Meal-Based Diet Plans</h3>
                {dietPlans && dietPlans.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {dietPlans.map((plan, index) => (
                      
                      <div key={index} className="border rounded-lg p-4 bg-gradient-to-br from-green-50 to-white shadow hover:shadow-md transition"
                      onClick={() => {
                  setSelectedDietPlan(plan);
                  setShowDietPlanDialog(true);
                }}>
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold text-lg text-green-800">{plan.name}</h4>
                            <p className="text-sm text-gray-600">{plan.meals.length} meals</p>
                          </div>
                                                     <Button
                             variant="outline"
                             size="sm"
                             onClick={(e) => {
                               e.stopPropagation(); // Prevent opening modal when clicking delete
                               handleConfirmDelete('meal-diet', index);
                             }}
                             className="text-red-500 hover:text-red-700"
                           >
                             <Trash2 className="h-4 w-4" />
                           </Button>
                        </div>
                        <div className="text-sm text-gray-600">
                          <p>Daily Target: {plan.dailyCalorieTarget} cal</p>
                          <p>Status: {plan.status}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">No meal-based diet plans created yet.</p>
                )}
              </div>

              {/* Create Buttons */}
              <div className="flex gap-4 pt-4 border-t">
                <Button onClick={handleCreateDietPlan} className="transition-all hover:scale-105 active:scale-95 bg-blue-600 hover:bg-blue-700">
                  <Plus className="mr-2 h-4 w-4" /> Create BMR Diet Plan
                </Button>
                <Button 
                  onClick={() => navigate('/diets')} 
                  className="transition-all hover:scale-105 active:scale-95 bg-green-600 hover:bg-green-700"
                >
                  <Plus className="mr-2 h-4 w-4" /> Create Meal Plan
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="workout" className="mt-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-indigo-700">Workout Plans</h3>
              <Button onClick={() => navigate('/workouts')} className="bg-green-600 hover:bg-green-700 text-white">
                <Plus className="mr-2 h-4 w-4" /> Create Workout Plan
              </Button>
            </div>
            {workoutPlans.length === 0 ? (
              <p className="text-muted-foreground italic">No workout plans assigned yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {workoutPlans.map((plan, index) => (
                  <div key={plan._id} className="border rounded-lg p-4 bg-gradient-to-br from-green-20 to-white shadow hover:shadow-md transition cursor-pointer"
                    onClick={() => handleViewWorkoutPlan(plan)}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-semibold text-lg text-indigo-800">{plan.name}</h4>
                        <p className="text-sm text-gray-600">{plan.status} • {plan.startDate?.slice(0,10)}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={e => { e.stopPropagation(); handleRemoveWorkout(plan, index); }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p>{plan.workoutSessions.length} days / sessions</p>
                      {plan.goals && <p>Goal: {plan.goals}</p>}
                      {plan.intensity && <p>Intensity: {plan.intensity}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Workout Plan View Dialog */}
          <Dialog open={showWorkoutPlanDialog} onOpenChange={setShowWorkoutPlanDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Workout Plan: {selectedWorkoutPlan?.name}</DialogTitle>
                <DialogDescription>
                  {selectedWorkoutPlan?.workoutSessions.length} days / sessions
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6 max-h-[60vh] overflow-y-auto">
                {selectedWorkoutPlan?.workoutSessions.map((session, idx) => (
                  <div key={idx} className="border rounded-lg p-4 bg-gradient-to-br from-indigo-30 to-white shadow">
                    <div className="font-semibold text-lg mb-2 text-indigo-800">{session.name}</div>
                    {/* <div className="text-xs text-gray-500 mb-2">Target Day: {session.targetDay || 'Any'}</div> */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {session.exercises.map((ex, exIdx) => (
                        <div key={exIdx} className="rounded-lg border bg-white p-3 shadow-sm hover:shadow-md transition">
                          <div className="font-medium text-base mb-1 text-primary">
                            Exercise: {exercises.find(e => e._id === ex.exercise)?.name || ex.exercise}
                          </div>
                          <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-sm">
                            <span>Sets:</span> <span>{ex.sets}</span>
                            <span>Reps:</span> <span>{ex.reps}</span>
                            {ex.weight !== undefined && <><span>Weight:</span> <span>{ex.weight} kg</span></>}
                            {ex.duration !== undefined && <><span>Duration:</span> <span>{ex.duration} sec</span></>}
                            {ex.restTime !== undefined && <><span>Rest:</span> <span>{ex.restTime} sec</span></>}
                            {ex.notes && <><span>Notes:</span> <span>{ex.notes}</span></>}
                          </div>
                        </div>
                      ))}
                    </div>
                    {session.warmup && <div className="mt-2 text-xs text-blue-700">Warmup: {session.warmup}</div>}
                    {session.cooldown && <div className="mt-2 text-xs text-blue-700">Cooldown: {session.cooldown}</div>}
                    {session.notes && <div className="mt-2 text-xs text-gray-600">Notes: {session.notes}</div>}
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowWorkoutPlanDialog(false)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {/* Remove Workout Plan Confirm Dialog */}
          <Dialog open={showRemoveWorkoutConfirm} onOpenChange={setShowRemoveWorkoutConfirm}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Remove Workout Plan</DialogTitle>
                <DialogDescription>
                  Are you sure you want to remove this workout plan from the client? This does not delete the plan itself.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowRemoveWorkoutConfirm(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={confirmRemoveWorkout}>
                  Remove
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
        
        <TabsContent value="medical" className="mt-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-4">
              {client.medicalInfo ? (
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-medium mb-2">Medical Conditions</h4>
                        <div className="flex flex-wrap gap-2">
                          {client.medicalInfo.conditions.map((condition, index) => (
                            <Badge key={index} variant="secondary">{condition}</Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium mb-2">Medications</h4>
                        <div className="flex flex-wrap gap-2">
                          {client.medicalInfo.medications.map((medication, index) => (
                            <Badge key={index} variant="secondary">{medication}</Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium mb-2">Allergies</h4>
                        <div className="flex flex-wrap gap-2">
                          {client.medicalInfo.allergies.map((allergy, index) => (
                            <Badge key={index} variant="secondary">{allergy}</Badge>
                          ))}
                        </div>
                      </div>
                      {client.medicalInfo.bloodType && (
                        <div>
                          <h4 className="text-sm font-medium mb-2">Medical Test</h4>
                          <p className="text-sm">{client.medicalInfo.bloodType}</p>
                        </div>
                      )}
                      {client.medicalInfo.reassessmentDate && (
                        <div>
                          <h4 className="text-sm font-medium mb-2">Reassessment Date</h4>
                          <p className="text-sm">{new Date(client.medicalInfo.reassessmentDate).toLocaleDateString()}</p>
                        </div>
                      )}
                      {client.medicalInfo.notes && (
                        <div>
                          <h4 className="text-sm font-medium mb-2">Notes</h4>
                          <p className="text-sm text-gray-600">{client.medicalInfo.notes}</p>
                        </div>
                      )}
                      <p className="text-xs text-gray-500">
                        Last updated: {new Date(client.medicalInfo.lastUpdated).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                                  setMedicalData({
          conditions: client.medicalInfo.conditions.join(', '),
          medications: client.medicalInfo.medications.join(', '),
          allergies: client.medicalInfo.allergies.join(', '),
          bloodType: client.medicalInfo.bloodType || '',
          reassessmentDate: client.medicalInfo.reassessmentDate || '',
          notes: client.medicalInfo.notes || ''
        });
                          setIsEditingMedical(true);
                          setShowMedicalDialog(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleConfirmDelete('medical', 0)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground italic">No medical information recorded yet.</p>
              )}
              <Button onClick={() => setShowMedicalDialog(true)} className="transition-all hover:scale-105 active:scale-95">
                <Plus className="mr-2 h-4 w-4" /> Add Medical Information
              </Button>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="gallery" className="mt-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-4">
              {client.gallery && client.gallery.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {galleryImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={resolveUploadUrl(image.url)}
                        alt={`Gallery image ${index + 1}`}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleImageClick(image.url)}
                          className="bg-white/90 hover:bg-white text-black"
                        >
                          <Maximize2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleConfirmDelete('gallery', index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          {new Date(image.date).toLocaleDateString()}
                        </p>
                        {image.notes && (
                          <p className="text-sm text-gray-600 mt-1">{image.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground italic">No images uploaded yet.</p>
              )}
              <Button
                onClick={() => setShowGalleryDialog(true)}
                className="transition-all hover:scale-105 active:scale-95"
              >
                <Upload className="mr-2 h-4 w-4" /> Upload Images
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={showObservationDialog} onOpenChange={(open) => {
        setShowObservationDialog(open);
        if (!open) {
          setIsEditingObservation(false);
          setSelectedObservation(null);
          setObservationData({ date: '', notes: '' });
        }
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{isEditingObservation ? 'Edit Observation' : 'Add Observation'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="observation-date" className="text-right">
                Date*
              </Label>
              <Input
                id="observation-date"
                type="date"
                className="col-span-3"
                value={observationData.date}
                onChange={(e) => setObservationData(prev => ({ ...prev, date: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="observation-notes" className="text-right">
                Notes*
              </Label>
              <Input
                id="observation-notes"
                className="col-span-3"
                value={observationData.notes}
                onChange={(e) => setObservationData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowObservationDialog(false);
              setIsEditingObservation(false);
              setSelectedObservation(null);
              setObservationData({ date: '', notes: '' });
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveObservation}>
              {isEditingObservation ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Health Note Dialog */}
      <Dialog open={showHealthNoteDialog} onOpenChange={(open) => {
        setShowHealthNoteDialog(open);
        if (!open) {
          setIsEditingHealthNote(false);
          setSelectedHealthNote(null);
          setHealthNoteData({ title: '', content: '', categories: [] });
        }
      }}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditingHealthNote ? 'Edit Health Note' : 'Add Health Note'}</DialogTitle>
            <DialogDescription>
              {isEditingHealthNote ? 'Edit the health note below' : 'Select categories to auto-fill guidelines, or write a custom note'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label className="mb-2 block">Categories</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { value: 'stomach', label: 'Stomach' },
                  { value: 'diabetes', label: 'Diabetes' },
                  { value: 'cholesterol', label: 'Cholesterol' },
                  { value: 'ibs', label: 'IBS' },
                  { value: 'blood-pressure', label: 'Blood Pressure' },
                  { value: 'anemia', label: 'Anemia' },
                  { value: 'kidney', label: 'Kidney' },
                  { value: 'custom', label: 'Custom' },
                ].map(cat => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => handleHealthNoteCategoryToggle(cat.value)}
                    className={`px-3 py-2 rounded-md border text-sm font-medium transition-all ${
                      healthNoteData.categories.includes(cat.value)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-foreground border-input hover:bg-accent'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="healthnote-title" className="text-right">
                Title
              </Label>
              <Input
                id="healthnote-title"
                className="col-span-3"
                placeholder="Health note title"
                value={healthNoteData.title}
                onChange={(e) => setHealthNoteData(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="healthnote-content" className="text-right mt-2">
                Content
              </Label>
              <textarea
                id="healthnote-content"
                className="col-span-3 flex min-h-[250px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                dir="rtl"
                placeholder="Write your health note here..."
                value={healthNoteData.content}
                onChange={(e) => setHealthNoteData(prev => ({ ...prev, content: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => {
              setShowHealthNoteDialog(false);
              setIsEditingHealthNote(false);
              setSelectedHealthNote(null);
              setHealthNoteData({ title: '', content: '', categories: [] });
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveHealthNote}>
              {isEditingHealthNote ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showMeasurementsDialog} onOpenChange={(open) => {
        setShowMeasurementsDialog(open);
        if (!open) {
          setIsEditingMeasurement(false);
          setSelectedMeasurement(null);
          setMeasurementData({
            date: '',
            weight: '',
            bodyFat: '',
            chest: '',
            waist: '',
            hips: '',
            arms: '',
            thighs: ''
          });
        }
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{isEditingMeasurement ? 'Edit Measurement' : 'Record Measurements'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="measurement-date" className="text-right">
                  Date*
                </Label>
                <Input
                  id="measurement-date"
                  type="date"
                  className="col-span-3"
                  value={measurementData.date}
                  onChange={(e) => setMeasurementData(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="measurement-weight" className="text-right">
                  Weight (kg)*
                </Label>
                <Input
                  id="measurement-weight"
                  type="number"
                  step="0.1"
                  className="col-span-3"
                  value={measurementData.weight}
                  onChange={(e) => setMeasurementData(prev => ({ ...prev, weight: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="measurement-bodyfat" className="text-right">
                  Body Fat %
                </Label>
                <Input
                  id="measurement-bodyfat"
                  type="number"
                  step="0.1"
                  className="col-span-3"
                  value={measurementData.bodyFat}
                  onChange={(e) => setMeasurementData(prev => ({ ...prev, bodyFat: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="measurement-chest" className="text-right">
                  Chest (cm)
                </Label>
                <Input
                  id="measurement-chest"
                  type="number"
                  step="0.1"
                  className="col-span-3"
                  value={measurementData.chest}
                  onChange={(e) => setMeasurementData(prev => ({ ...prev, chest: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="measurement-waist" className="text-right">
                  Waist (cm)
                </Label>
                <Input
                  id="measurement-waist"
                  type="number"
                  step="0.1"
                  className="col-span-3"
                  value={measurementData.waist}
                  onChange={(e) => setMeasurementData(prev => ({ ...prev, waist: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="measurement-hips" className="text-right">
                  Hips (cm)
                </Label>
                <Input
                  id="measurement-hips"
                  type="number"
                  step="0.1"
                  className="col-span-3"
                  value={measurementData.hips}
                  onChange={(e) => setMeasurementData(prev => ({ ...prev, hips: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="measurement-arms" className="text-right">
                  Arms (cm)
                </Label>
                <Input
                  id="measurement-arms"
                  type="number"
                  step="0.1"
                  className="col-span-3"
                  value={measurementData.arms}
                  onChange={(e) => setMeasurementData(prev => ({ ...prev, arms: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="measurement-thighs" className="text-right">
                  Thighs (cm)
                </Label>
                <Input
                  id="measurement-thighs"
                  type="number"
                  step="0.1"
                  className="col-span-3"
                  value={measurementData.thighs}
                  onChange={(e) => setMeasurementData(prev => ({ ...prev, thighs: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => {
              setShowMeasurementsDialog(false);
              setIsEditingMeasurement(false);
              setSelectedMeasurement(null);
              setMeasurementData({
                date: '',
                weight: '',
                bodyFat: '',
                chest: '',
                waist: '',
                hips: '',
                arms: '',
                thighs: ''
              });
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveMeasurements}>
              {isEditingMeasurement ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDietDialog} onOpenChange={(open) => {
        setShowDietDialog(open);
        if (!open) {
          setIsEditingBmr(false);
          setSelectedBmr(null);
        }
      }}>
        <DialogContent className="w-[95vw] max-w-[95vw] bg-white rounded-2xl shadow-xl p-6 overflow-y-auto max-h-[90vh]">
                  <DialogHeader>
          <DialogTitle>{isEditingBmr ? 'Edit BMR Diet Plan' : 'Create Diet Plan'}</DialogTitle>
        </DialogHeader>
          <Tabs defaultValue={isEditingBmr && selectedBmr ? selectedBmr.equationMethod : "mifflin"} className="w-full mt-4">
            <TabsList className="grid grid-cols-3 w-full mb-6">
              <TabsTrigger value="mifflin">Mifflin-St Jeor</TabsTrigger>
              <TabsTrigger value="harris">Revised Harris-Benedict</TabsTrigger>
              <TabsTrigger value="katch">Katch-McArdle</TabsTrigger>
            </TabsList>
            <TabsContent value="mifflin">
              <BmrWorksheet equation="mifflin" onSave={handleSaveDietPlan} editingData={isEditingBmr ? selectedBmr : undefined} />
            </TabsContent>
            <TabsContent value="harris">
              <BmrWorksheet equation="harris" onSave={handleSaveDietPlan} editingData={isEditingBmr ? selectedBmr : undefined} />
            </TabsContent>
            <TabsContent value="katch">
              <BmrWorksheet equation="katch" onSave={handleSaveDietPlan} editingData={isEditingBmr ? selectedBmr : undefined} />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <Dialog open={showMedicalDialog} onOpenChange={(open) => {
        setShowMedicalDialog(open);
        if (!open) {
          setIsEditingMedical(false);
          setMedicalData({
            conditions: '',
            medications: '',
            allergies: '',
            bloodType: '',
            reassessmentDate: '',
            notes: ''
          });
        }
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{isEditingMedical ? 'Edit Medical Information' : 'Add Medical Information'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="medical-conditions" className="text-right">
                Conditions
              </Label>
              <Input
                id="medical-conditions"
                className="col-span-3"
                placeholder="Enter conditions (comma-separated)"
                value={medicalData.conditions}
                onChange={(e) => setMedicalData(prev => ({ ...prev, conditions: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="medical-medications" className="text-right">
                Medications
              </Label>
              <Input
                id="medical-medications"
                className="col-span-3"
                placeholder="Enter medications (comma-separated)"
                value={medicalData.medications}
                onChange={(e) => setMedicalData(prev => ({ ...prev, medications: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="medical-allergies" className="text-right">
                Allergies
              </Label>
              <Input
                id="medical-allergies"
                className="col-span-3"
                placeholder="Enter allergies (comma-separated)"
                value={medicalData.allergies}
                onChange={(e) => setMedicalData(prev => ({ ...prev, allergies: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="medical-bloodType" className="text-right">
                Medical Test
              </Label>
              <Input
                id="medical-bloodType"
                className="col-span-3"
                placeholder="Enter Medical Test"
                value={medicalData.bloodType}
                onChange={(e) => setMedicalData(prev => ({ ...prev, bloodType: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="medical-reassessmentDate" className="text-right">
                Reassessment Date
              </Label>
              <Input
                id="medical-reassessmentDate"
                type="date"
                className="col-span-3"
                value={medicalData.reassessmentDate}
                onChange={(e) => setMedicalData(prev => ({ ...prev, reassessmentDate: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="medical-notes" className="text-right">
                Notes
              </Label>
              <Input
                id="medical-notes"
                className="col-span-3"
                placeholder="Enter additional notes"
                value={medicalData.notes}
                onChange={(e) => setMedicalData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowMedicalDialog(false);
              setIsEditingMedical(false);
              setMedicalData({
                conditions: '',
                medications: '',
                allergies: '',
                bloodType: '',
                reassessmentDate: '',
                notes: ''
              });
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveMedicalInfo}>
              {isEditingMedical ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Dialog */}
      <Dialog open={showSubscriptionDialog} onOpenChange={(open) => {
        setShowSubscriptionDialog(open);
        if (!open && client) {
          setSubscriptionData({
            startDate: client.subscription?.startDate ? new Date(client.subscription.startDate).toISOString().slice(0,10) : '',
            endDate: client.subscription?.endDate ? new Date(client.subscription.endDate).toISOString().slice(0,10) : '',
            note: client.subscription?.note || ''
          });
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Set Subscription</DialogTitle>
            <DialogDescription>Choose start/end dates and an optional note.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Start Date</Label>
              <Input type="date" className="col-span-3" value={subscriptionData.startDate} onChange={(e) => setSubscriptionData(prev => ({ ...prev, startDate: e.target.value }))} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">End Date</Label>
              <Input type="date" className="col-span-3" value={subscriptionData.endDate} onChange={(e) => setSubscriptionData(prev => ({ ...prev, endDate: e.target.value }))} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Note</Label>
              <Input className="col-span-3" placeholder="Optional note" value={subscriptionData.note} onChange={(e) => setSubscriptionData(prev => ({ ...prev, note: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubscriptionDialog(false)}>Cancel</Button>
            <Button onClick={async () => {
              try {
                const payload: any = { subscription: undefined as any };
                payload.subscription = {
                  startDate: subscriptionData.startDate ? new Date(subscriptionData.startDate) : undefined,
                  endDate: subscriptionData.endDate ? new Date(subscriptionData.endDate) : undefined,
                  note: subscriptionData.note || undefined,
                };
                const updated = await clientApi.update(id || '', payload);
                setClient(updated);
                setShowSubscriptionDialog(false);
                toast({ title: 'Subscription Updated', description: 'Subscription settings saved successfully.' });
              } catch (err) {
                toast({ title: 'Error', description: 'Failed to save subscription.', variant: 'destructive' });
              }
            }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* InBody Upload Dialog */}
      <Dialog open={showInBodyDialog} onOpenChange={(open) => {
        setShowInBodyDialog(open);
        if (!open) {
          setIsEditingInBody(false);
          setSelectedInBody(null);
          setInBodyData({
            date: '',
            weight: '',
            skeletalMuscle: '',
            bodyFat: '',
            bmi: '',
            bodyFatMass: '',
            pbf: '',
            minerals: '',
            protein: '',
            notes: '',
            fileUrl: ''
          });
        }
      }}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>{isEditingInBody ? 'Edit InBody Results' : 'Upload InBody Results'}</DialogTitle>
            <DialogDescription>
              Enter InBody measurements and upload the report file (PDF or image)
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Date and File Upload Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="inbody-date">Date*</Label>
                <Input
                  id="inbody-date"
                  type="date"
                  value={inBodyData.date}
                  onChange={handleInBodyInputChange}
                  required
                />
              </div>
                              <div className="space-y-2">
                  <Label htmlFor="inbody-file">Report File (PDF/Image)*</Label>
                  <Input
                    id="inbody-file"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.gif"
                    onChange={handleInBodyFileChange}
                    required
                  />
                  {inBodyData.fileUrl && (
                    <div className="mt-2">
                      <div className="text-xs text-green-600 mb-1">
                        ✓ File uploaded successfully
                      </div>
                      {isPdfUrl(inBodyData.fileUrl) ? (
                        <div className="text-xs text-blue-600">
                          📄 PDF Report: {inBodyData.fileUrl.split('/').pop()}
                        </div>
                      ) : (
                        <div className="text-xs text-blue-600">
                          🖼️ Image: {inBodyData.fileUrl.split('/').pop()}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => openInBodyFile(inBodyData.fileUrl)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        View File
                      </button>
                    </div>
                  )}
                </div>
            </div>

            {/* Basic Measurements Row */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="inbody-weight">Weight (kg)*</Label>
                <Input
                  id="inbody-weight"
                  type="number"
                  step="0.1"
                  value={inBodyData.weight}
                  onChange={handleInBodyInputChange}
                  placeholder="70.5"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inbody-bodyfat">Body Fat %*</Label>
                <Input
                  id="inbody-bodyfat"
                  type="number"
                  step="0.1"
                  value={inBodyData.bodyFat}
                  onChange={handleInBodyInputChange}
                  placeholder="15.2"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inbody-muscle">Skeletal Muscle (kg)</Label>
                <Input
                  id="inbody-muscle"
                  type="number"
                  step="0.1"
                  value={inBodyData.skeletalMuscle}
                  onChange={handleInBodyInputChange}
                  placeholder="28.5"
                />
              </div>
            </div>

            {/* Body Fat Mass and Notes Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="inbody-fatmass">Body Fat Mass (kg)</Label>
                <Input
                  id="inbody-fatmass"
                  type="number"
                  step="0.1"
                  value={inBodyData.bodyFatMass}
                  onChange={handleInBodyInputChange}
                  placeholder="10.8"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inbody-notes">Notes</Label>
                <Input
                  id="inbody-notes"
                  value={inBodyData.notes}
                  onChange={handleInBodyInputChange}
                  placeholder="Additional observations or notes"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowInBodyDialog(false);
              setIsEditingInBody(false);
              setSelectedInBody(null);
              setInBodyData({
                date: '',
                weight: '',
                skeletalMuscle: '',
                bodyFat: '',
                bmi: '',
                bodyFatMass: '',
                pbf: '',
                minerals: '',
                protein: '',
                notes: '',
                fileUrl: ''
              });
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveInBody}>
              {isEditingInBody ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showGalleryDialog} onOpenChange={(open) => {
        setShowGalleryDialog(open);
        if (!open) {
          setGalleryDate('');
          setGalleryNotes('');
          setSelectedFiles([]);
          setPreviewUrls([]);
        }
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Upload Photos</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="gallery-date" className="text-right">
                Date*
              </Label>
              <Input
                id="gallery-date"
                type="date"
                className="col-span-3"
                value={galleryDate}
                onChange={(e) => setGalleryDate(e.target.value)}
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="gallery-photos" className="text-right">
                Photos*
              </Label>
              <Input
                id="gallery-photos"
                type="file"
                className="col-span-3"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
              />
            </div>
            {previewUrls.length > 0 && (
              <div className="grid grid-cols-4 items-start gap-4">
                <Label className="text-right">
                  Preview
                </Label>
                <div className="col-span-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {previewUrls.map((url, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="gallery-notes" className="text-right">
                Notes
              </Label>
              <Input
                id="gallery-notes"
                className="col-span-3"
                placeholder="Enter notes for these photos"
                value={galleryNotes}
                onChange={(e) => setGalleryNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowGalleryDialog(false);
              setGalleryDate('');
              setGalleryNotes('');
              setSelectedFiles([]);
              setPreviewUrls([]);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveGallery}>
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showInBodyViewDialog} onOpenChange={setShowInBodyViewDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>InBody Results</DialogTitle>
            <DialogDescription>
              {selectedInBody && new Date(selectedInBody.date).toLocaleDateString()}
            </DialogDescription>
          </DialogHeader>
          {selectedInBody && (
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">Basic Measurements</h4>
                  <div className="space-y-2">
                    <p className="text-sm">Weight: {selectedInBody.weight} kg</p>
                    <p className="text-sm">Body Fat: {selectedInBody.bodyFat}%</p>
                    <p className="text-sm">BMI: {selectedInBody.bmi}</p>
                    <p className="text-sm">Skeletal Muscle: {selectedInBody.skeletalMuscle} kg</p>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2">Composition Analysis</h4>
                  <div className="space-y-2">
                    <p className="text-sm">Body Fat Mass: {selectedInBody.bodyFatMass} kg</p>
                    <p className="text-sm">PBF: {selectedInBody.pbf}%</p>
                    <p className="text-sm">Minerals: {selectedInBody.minerals} kg</p>
                    <p className="text-sm">Protein: {selectedInBody.protein} kg</p>
                  </div>
                </div>
              </div>
              {selectedInBody.notes && (
                <div className="col-span-2">
                  <h4 className="text-sm font-medium mb-2">Notes</h4>
                  <p className="text-sm text-gray-600">{selectedInBody.notes}</p>
                </div>
              )}
              {selectedInBody.fileUrl && (
                <div className="col-span-2">
                  <h4 className="text-sm font-medium mb-2">Report</h4>
                  {!(isPdfUrl(selectedInBody.fileUrl)) && (
                    <img
                      src={resolveUploadUrl(selectedInBody.fileUrl)}
                      alt="InBody report"
                      className="max-h-72 rounded-md border mb-2 cursor-pointer hover:opacity-90"
                      onClick={() => openInBodyFile(selectedInBody.fileUrl)}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => openInBodyFile(selectedInBody.fileUrl)}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    {isPdfUrl(selectedInBody.fileUrl) ? 'Open PDF Report' : 'Open InBody Image'}
                  </button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInBodyViewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this item? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirmDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteItem}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[900px]">
          <DialogHeader>
            <DialogTitle>Edit Client Information</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Name*
                </Label>
                <Input
                  id="name"
                  className="col-span-3"
                  value={editFormData.name}
                  onChange={handleEditInputChange}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="email" className="text-right">
                  Email*
                </Label>
                <Input
                  id="email"
                  type="email"
                  className="col-span-3"
                  value={editFormData.email}
                  onChange={handleEditInputChange}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="age" className="text-right">
                  Age*
                </Label>
                <Input
                  id="age"
                  type="number"
                  className="col-span-3"
                  value={editFormData.age}
                  onChange={handleEditInputChange}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="phone" className="text-right">
                  Phone
                </Label>
                <Input
                  id="phone"
                  className="col-span-3"
                  value={editFormData.phone}
                  onChange={handleEditInputChange}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="gender" className="text-right">
                  Gender*
                </Label>
                <Select value={editFormData.gender} onValueChange={handleEditSelectChange}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="profession" className="text-right">
                  Profession
                </Label>
                <Input
                  id="profession"
                  className="col-span-3"
                  value={editFormData.profession}
                  onChange={handleEditInputChange}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="hasWhatsApp" className="text-right">
                  Has WhatsApp
                </Label>
                <div className="col-span-3 flex items-center">
                  <Input
                    id="hasWhatsApp"
                    type="checkbox"
                    className="w-4 h-4"
                    checked={editFormData.hasWhatsApp}
                    onChange={handleEditInputChange}
                  />
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="height" className="text-right">
                  Height (cm)
                </Label>
                <Input
                  id="height"
                  type="number"
                  className="col-span-3"
                  value={editFormData.height}
                  onChange={handleEditInputChange}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="weight" className="text-right">
                  Weight (kg)
                </Label>
                <Input
                  id="weight"
                  type="number"
                  className="col-span-3"
                  value={editFormData.weight}
                  onChange={handleEditInputChange}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="goal" className="text-right">
                  Goal
                </Label>
                <Input
                  id="goal"
                  className="col-span-3"
                  value={editFormData.goal}
                  onChange={handleEditInputChange}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="dietType" className="text-right">
                  Diet Type
                </Label>
                <Input
                  id="dietType"
                  className="col-span-3"
                  value={editFormData.dietType}
                  onChange={handleEditInputChange}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="allergies" className="text-right">
                  Allergies
                </Label>
                <Input
                  id="allergies"
                  className="col-span-3"
                  placeholder="Comma-separated list"
                  value={editFormData.allergies}
                  onChange={handleEditInputChange}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="foodPreferences" className="text-right">
                  Food Preferences
                </Label>
                <Input
                  id="foodPreferences"
                  className="col-span-3"
                  value={editFormData.foodPreferences}
                  onChange={handleEditInputChange}
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditClient}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Screen Image Viewer Dialog */}
      <Dialog open={showImageViewerDialog} onOpenChange={setShowImageViewerDialog}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/90 border-none">
          <div className="relative w-full h-full flex items-center justify-center">
            {selectedImage && (
              <img
                src={selectedImage}
                alt="Full size"
                className="max-w-full max-h-[90vh] object-contain"
              />
            )}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white"
              onClick={() => setShowImageViewerDialog(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Diet Plans Section */}
    
      {/* Diet Plan View Dialog */}
      <Dialog open={showDietPlanDialog} onOpenChange={setShowDietPlanDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Diet Plan: {selectedDietPlan?.name}</DialogTitle>
            <DialogDescription>
              {selectedDietPlan?.meals.length} meals
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 max-h-[60vh] overflow-y-auto">
            {selectedDietPlan?.meals.map((meal, idx) => (
              <div key={idx} className="border rounded-lg p-4 bg-gradient-to-br from-green-50 to-white shadow">
                <div className="font-semibold text-lg mb-2 text-green-800">{meal.name}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {meal.foods.map((food, fidx) => (
                    <div key={fidx} className="rounded-lg border bg-white p-3 shadow-sm hover:shadow-md transition">
                      <div className="font-medium text-base mb-1 text-primary">{food.name}</div>
                      <div className="text-xs text-muted-foreground mb-2">{food.category}</div>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-sm">
                        <span>Quantity:</span> <span>{food.quantity} {food.unit || 'g'}</span>
                        {food.calories !== undefined && <><span>Calories:</span> <span>{food.calories} kcal</span></>}
                        {food.protein !== undefined && <><span>Protein:</span> <span>{food.protein} g</span></>}
                        {food.carbs !== undefined && <><span>Carbs:</span> <span>{food.carbs} g</span></>}
                        {food.fat !== undefined && <><span>Fat:</span> <span>{food.fat} g</span></>}
                        {food.fiber !== undefined && <><span>Fiber:</span> <span>{food.fiber} g</span></>}
                        {food.sodium !== undefined && <><span>Sodium:</span> <span>{food.sodium} mg</span></>}
                        {food.potassium !== undefined && <><span>Potassium:</span> <span>{food.potassium} mg</span></>}
                        {food.calcium !== undefined && <><span>Calcium:</span> <span>{food.calcium} mg</span></>}
                        {food.iron !== undefined && <><span>Iron:</span> <span>{food.iron} mg</span></>}
                        {food.notes && <><span>Notes:</span> <span>{food.notes}</span></>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDietPlanDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDetail;

function BmrWorksheet({ equation, onSave, editingData }: { 
  equation: 'mifflin' | 'harris' | 'katch'; 
  onSave: (data: any) => void;
  editingData?: any;
}) {
  const [weight, setWeight] = useState(editingData?.weight?.toString() || '');
  const [height, setHeight] = useState(editingData?.height?.toString() || '');
  const [age, setAge] = useState(editingData?.age?.toString() || '');
  const [gender, setGender] = useState(editingData?.gender || 'male');
  const [factor1, setFactor1] = useState(editingData?.factor1?.toString() || '10');
  const [factor2, setFactor2] = useState(editingData?.factor2?.toString() || '6.25');
  const [factor3, setFactor3] = useState(editingData?.factor3?.toString() || '5');
  const [factor4, setFactor4] = useState(editingData?.factor4?.toString() || '5');
  const [bmr, setBmr] = useState(editingData?.bmr?.toString() || '');
  const [calcError, setCalcError] = useState('');
  const [activity, setActivity] = useState(editingData?.activityLevel?.toString() || '1.2');
  const [proteinPercent, setProteinPercent] = useState(editingData?.protein ? ((editingData.protein * 4 / editingData.calories) * 100).toFixed(1) : '');
  const [carbPercent, setCarbPercent] = useState(editingData?.carbs ? ((editingData.carbs * 4 / editingData.calories) * 100).toFixed(1) : '');
  const [fatPercent, setFatPercent] = useState(editingData?.fats ? ((editingData.fats * 9 / editingData.calories) * 100).toFixed(1) : '');
  const [fatBody, setFatBody] = useState(editingData?.bodyFat?.toString() || ''); // For Katch-McArdle
  const [deficitCalories, setDeficitCalories] = useState('0'); // Calorie deficit/surplus input
  const [dietPlanName, setDietPlanName] = useState(editingData?.name || '');
  const [dietPlanDate, setDietPlanDate] = useState(editingData?.date || '');
  const [workoutPlans, setWorkoutPlans] = useState<WorkoutPlan[]>([]);
  const [showWorkoutPlanDialog, setShowWorkoutPlanDialog] = useState(false);
  const [selectedWorkoutPlan, setSelectedWorkoutPlan] = useState<WorkoutPlan | null>(null);
  const [removingWorkoutIndex, setRemovingWorkoutIndex] = useState<number | null>(null);
  const [showRemoveWorkoutConfirm, setShowRemoveWorkoutConfirm] = useState(false);
  const [exercises, setExercises] = useState<Exercise[]>([]);

  const baseCalories = bmr && activity && !isNaN(Number(bmr)) && !isNaN(Number(activity)) ? Math.round(Number(bmr) * Number(activity)) : 0;
  const calories = baseCalories && !isNaN(Number(deficitCalories)) ? Math.round(baseCalories + Number(deficitCalories)) : baseCalories;
  const proteinGrams = proteinPercent && calories && !isNaN(Number(proteinPercent)) ? ((Number(proteinPercent) / 100) * calories / 4).toFixed(1) : '';
  const fatGrams = fatPercent && calories && !isNaN(Number(fatPercent)) ? ((Number(fatPercent) / 100) * calories / 9).toFixed(1) : '';
  const carbGrams = carbPercent && calories && !isNaN(Number(carbPercent)) ? ((Number(carbPercent) / 100) * calories / 4).toFixed(1) : '';

  // Update factors when gender changes
  useEffect(() => {
    if (equation === 'mifflin') {
      if (gender === 'male') {
        setFactor1('10');
        setFactor2('6.25');
        setFactor3('5');
        setFactor4('5');
      } else if (gender === 'female') {
        setFactor1('10');
        setFactor2('6.25');
        setFactor3('5');
        setFactor4('-161');
      }
    } else if (equation === 'harris') {
      if (gender === 'male') {
        setFactor1('13.397');
        setFactor2('4.799');
        setFactor3('5.677');
        setFactor4('88.362');
      } else if (gender === 'female') {
        setFactor1('9.247');
        setFactor2('3.098');
        setFactor3('4.330');
        setFactor4('447.593');
      }
    } else if (equation === 'katch') {
      setFactor1('370');
      setFactor2('21.6');
    }
  }, [gender, equation]);

  // Handle editing data changes
  useEffect(() => {
    if (editingData) {
      setWeight(editingData.weight?.toString() || '');
      setHeight(editingData.height?.toString() || '');
      setAge(editingData.age?.toString() || '');
      setGender(editingData.gender || 'male');
      setFactor1(editingData.factor1?.toString() || '10');
      setFactor2(editingData.factor2?.toString() || '6.25');
      setFactor3(editingData.factor3?.toString() || '5');
      setFactor4(editingData.factor4?.toString() || '5');
      setBmr(editingData.bmr?.toString() || '');
      setActivity(editingData.activityLevel?.toString() || '1.2');
      setProteinPercent(editingData.protein ? ((editingData.protein * 4 / editingData.calories) * 100).toFixed(1) : '');
      setCarbPercent(editingData.carbs ? ((editingData.carbs * 4 / editingData.calories) * 100).toFixed(1) : '');
      setFatPercent(editingData.fats ? ((editingData.fats * 9 / editingData.calories) * 100).toFixed(1) : '');
      setFatBody(editingData.bodyFat?.toString() || '');
      setDietPlanName(editingData.name || '');
      setDietPlanDate(editingData.date || '');
    }
  }, [editingData]);

  // Live calculation (add Katch-McArdle logic)
  useEffect(() => {
    let result = '';
    let error = '';
    try {
      const w = parseFloat(weight);
      const h = parseFloat(height);
      const a = parseFloat(age);
      const f1 = parseFloat(factor1);
      const f2 = parseFloat(factor2);
      const f3 = parseFloat(factor3);
      const f4 = parseFloat(factor4);
      const fat = parseFloat(fatBody);
      if (equation === 'katch') {
        if ([w, fat, f1, f2].some(x => !isFinite(x))) {
          setBmr('');
          setCalcError('Please enter valid numbers for all fields.');
          return;
        }
        // BMR = 370 + 21.6 * (1 - fat%) * weight
        result = Math.round(f1 + f2 * (1 - fat / 100) * w).toString();
        error = '';
      } else {
        if ([w, h, a, f1, f2, f3, f4].some(x => !isFinite(x))) {
          setBmr('');
          setCalcError('Please enter valid numbers for all fields.');
          return;
        }
        result = Math.round(f1 * w + f2 * h - f3 * a + f4).toString();
        error = '';
      }
    } catch (e) {
      result = '';
      error = 'Could not calculate BMR.';
    }
    setBmr(result);
    setCalcError(error);
  }, [weight, height, age, factor1, factor2, factor3, factor4, fatBody, equation]);

  const handleSave = () => {
    if (!dietPlanName || !dietPlanDate || !bmr || !calories) {
      return;
    }

    const dietData = {
      date: dietPlanDate,
      name: dietPlanName,
      type: `${equation.toUpperCase()} BMR Plan`,
      weight: parseFloat(weight) || 0,
      height: parseFloat(height) || 0,
      age: parseFloat(age) || 0,
      gender: gender,
      equationMethod: equation,
      bodyFat: parseFloat(fatBody) || 0,
      factor1: parseFloat(factor1) || 0,
      factor2: parseFloat(factor2) || 0,
      factor3: parseFloat(factor3) || 0,
      factor4: parseFloat(factor4) || 0,
      bmr: parseFloat(bmr) || 0,
      activityLevel: parseFloat(activity) || 1.2,
      calories: calories,
      protein: parseFloat(proteinGrams) || 0,
      carbs: parseFloat(carbGrams) || 0,
      fats: parseFloat(fatGrams) || 0,
      notes: `Equation: ${equation}, Activity Factor: ${activity}, Calorie Adjustment: ${deficitCalories}, Protein: ${proteinPercent}%, Carbs: ${carbPercent}%, Fat: ${fatPercent}%`,
      createdAt: new Date().toISOString()
    };

    onSave(dietData);
  };

  if (equation === 'mifflin') {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Inputs & BMR */}
        <div className="space-y-4">
          {/* Basic Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium mb-3 text-sm">Basic Information</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="mifflin-weight" className="text-xs">Weight (kg)</Label>
                <Input 
                  id="mifflin-weight" 
                  type="number" 
                  value={weight} 
                  onChange={e => setWeight(e.target.value)} 
                  className="mt-1 h-8 text-sm" 
                  placeholder="70"
                />
              </div>
              <div>
                <Label htmlFor="mifflin-height" className="text-xs">Height (cm)</Label>
                <Input 
                  id="mifflin-height" 
                  type="number" 
                  value={height} 
                  onChange={e => setHeight(e.target.value)} 
                  className="mt-1 h-8 text-sm" 
                  placeholder="175"
                />
              </div>
              <div>
                <Label htmlFor="mifflin-age" className="text-xs">Age (years)</Label>
                <Input 
                  id="mifflin-age" 
                  type="number" 
                  value={age} 
                  onChange={e => setAge(e.target.value)} 
                  className="mt-1 h-8 text-sm" 
                  placeholder="25"
                />
              </div>
              <div>
                <Label htmlFor="mifflin-gender" className="text-xs">Gender</Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger className="mt-1 h-8 text-sm">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* BMR Result */}
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-xs text-gray-600 mb-1">BMR</div>
            <div className="text-3xl font-bold text-blue-600 mb-1">
              {bmr || '--'}
            </div>
            <div className="text-xs text-gray-500">cal/day</div>
          </div>

          {/* Activity Level */}
          <div className="bg-white rounded-lg border p-4">
            <Label className="text-xs font-medium">Activity Level</Label>
            <Select value={activity} onValueChange={setActivity}>
              <SelectTrigger className="mt-2 h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1.2">Sedentary (1.2)</SelectItem>
                <SelectItem value="1.3">Light (1.3)</SelectItem>
                <SelectItem value="1.5">Moderate (1.5)</SelectItem>
                <SelectItem value="1.7">Heavy (1.7)</SelectItem>
                <SelectItem value="1.8">Very Heavy (1.8)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Calorie Deficit/Surplus */}
          <div className="bg-white rounded-lg border p-4">
            <Label className="text-xs font-medium">Calorie Adjustment</Label>
            <div className="mt-2 space-y-2">
              <Input
                type="number"
                value={deficitCalories}
                onChange={e => setDeficitCalories(e.target.value)}
                className="h-8 text-sm"
                placeholder="0"
              />
              <div className="text-xs text-gray-500">
                <div>• Positive (+500): Bulk/Gain weight</div>
                <div>• Negative (-500): Cut/Lose weight</div>
                <div>• Zero (0): Maintain weight</div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Diet Plan Details */}
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-medium mb-3 text-sm">Diet Plan Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="diet-plan-name" className="text-xs">Plan Name*</Label>
                <Input 
                  id="diet-plan-name" 
                  type="text" 
                  value={dietPlanName} 
                  onChange={e => setDietPlanName(e.target.value)} 
                  className="mt-1 h-8 text-sm" 
                  placeholder="e.g., Weight Loss Plan"
                />
              </div>
              <div>
                <Label htmlFor="diet-plan-date" className="text-xs">Date*</Label>
                <Input 
                  id="diet-plan-date" 
                  type="date" 
                  value={dietPlanDate} 
                  onChange={e => setDietPlanDate(e.target.value)} 
                  className="mt-1 h-8 text-sm"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={handleSave} disabled={!dietPlanName || !dietPlanDate || !bmr || !calories}>
                {editingData ? 'Update Diet Plan' : 'Save Diet Plan'}
              </Button>
            </div>
          </div>

          {/* Top Row: Calories + Macros */}
          <div className="flex flex-wrap gap-4 w-full">
            {/* Calories Box */}
            <div className="flex-1 bg-green-50 rounded-lg p-8 text-center flex flex-col justify-center min-w-[300px]">
              <div className="text-xs text-gray-600 mb-1">Total Daily Calories</div>
              <div className="text-4xl font-bold text-green-700 mb-1">
                {calories || '--'}
              </div>
              <div className="text-xs text-gray-500 mb-2">kcal/day</div>
              {bmr && activity && (
                <div className="text-xs text-gray-600">
                  {bmr} × {activity} = {baseCalories}
                  {Number(deficitCalories) !== 0 && (
                    <div className="text-xs text-gray-600">
                      {baseCalories} {Number(deficitCalories) > 0 ? '+' : ''}{deficitCalories} = {calories}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Macros Box */}
            <div className="flex-1 bg-white rounded-lg border p-6 flex flex-col justify-between min-w-[300px] min-h-[220px]">
              <div>
                <h3 className="font-medium mb-3 text-sm">Macronutrients</h3>
                <div className="space-y-3">
                  {/* Protein */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-medium text-purple-700">Protein</div>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={proteinPercent} onChange={e => setProteinPercent(e.target.value)} className="w-20 h-6 text-xs text-center font-bold border-2 border-purple-200 bg-purple-50" />
                      <span className="text-xs">%</span>
                      <div className="w-16 text-right text-sm font-bold text-purple-600">
                        {proteinGrams || '--'}g
                      </div>
                    </div>
                  </div>

                  {/* Carbs */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-medium text-yellow-700">Carbs</div>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={carbPercent} onChange={e => setCarbPercent(e.target.value)} className="w-20 h-6 text-xs text-center font-bold border-2 border-yellow-200 bg-yellow-50" />
                      <span className="text-xs">%</span>
                      <div className="w-16 text-right text-sm font-bold text-yellow-600">
                        {carbGrams || '--'}g
                      </div>
                    </div>
                  </div>

                  {/* Fat */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-medium text-pink-700">Fat</div>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={fatPercent} onChange={e => setFatPercent(e.target.value)} className="w-20 h-6 text-xs text-center font-bold border-2 border-pink-200 bg-pink-50" />
                      <span className="text-xs">%</span>
                      <div className="w-16 text-right text-sm font-bold text-pink-600">
                        {fatGrams || '--'}g
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 text-xs text-gray-400 text-center">
                Protein/Carbs: 4 cal/g | Fat: 9 cal/g
              </div>
            </div>
          </div>

          {/* Bottom Row: BMR Formula */}
          <div className="w-full bg-blue-50 rounded-xl border border-blue-100 shadow p-6 flex flex-col items-center">
            <div className="text-base font-bold mb-2">Adjustable BMR Formula</div>
            <div className="flex flex-wrap items-center justify-center gap-2 text-lg font-bold">
              <Input type="number" value={factor1} onChange={e => setFactor1(e.target.value)} className="w-24 h-8 text-center font-bold bg-white border-2 border-blue-200 rounded-lg" />
              <span className="mx-1">×</span>
              <span className="font-semibold text-blue-700">W</span>
              <span className="mx-1">+</span>
              <Input type="number" value={factor2} onChange={e => setFactor2(e.target.value)} className="w-24 h-8 text-center font-bold bg-white border-2 border-blue-200 rounded-lg" />
              <span className="mx-1">×</span>
              <span className="font-semibold text-blue-700">H</span>
              <span className="mx-1">-</span>
              <Input type="number" value={factor3} onChange={e => setFactor3(e.target.value)} className="w-24 h-8 text-center font-bold bg-white border-2 border-blue-200 rounded-lg" />
              <span className="mx-1">×</span>
              <span className="font-semibold text-blue-700">A</span>
              <span className="mx-1">+</span>
              <Input type="number" value={factor4} onChange={e => setFactor4(e.target.value)} className="w-24 h-8 text-center font-bold bg-white border-2 border-blue-200 rounded-lg" />
            </div>
            <div className="text-xs text-gray-500 mt-2">W = Weight, H = Height, A = Age</div>
            {calcError && (
              <div className="text-red-600 text-xs mt-2 text-center">{calcError}</div>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  if (equation === 'harris') {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Inputs & BMR */}
        <div className="space-y-4">
          {/* Basic Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium mb-3 text-sm">Basic Information</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="harris-weight" className="text-xs">Weight (kg)</Label>
                <Input 
                  id="harris-weight" 
                  type="number" 
                  value={weight} 
                  onChange={e => setWeight(e.target.value)} 
                  className="mt-1 h-8 text-sm" 
                  placeholder="70"
                />
              </div>
              <div>
                <Label htmlFor="harris-height" className="text-xs">Height (cm)</Label>
                <Input 
                  id="harris-height" 
                  type="number" 
                  value={height} 
                  onChange={e => setHeight(e.target.value)} 
                  className="mt-1 h-8 text-sm" 
                  placeholder="175"
                />
              </div>
              <div>
                <Label htmlFor="harris-age" className="text-xs">Age (years)</Label>
                <Input 
                  id="harris-age" 
                  type="number" 
                  value={age} 
                  onChange={e => setAge(e.target.value)} 
                  className="mt-1 h-8 text-sm" 
                  placeholder="25"
                />
              </div>
              <div>
                <Label htmlFor="harris-gender" className="text-xs">Gender</Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger className="mt-1 h-8 text-sm">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* BMR Result */}
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-xs text-gray-600 mb-1">BMR</div>
            <div className="text-3xl font-bold text-blue-600 mb-1">
              {bmr || '--'}
            </div>
            <div className="text-xs text-gray-500">cal/day</div>
          </div>

          {/* Activity Level */}
          <div className="bg-white rounded-lg border p-4">
            <Label className="text-xs font-medium">Activity Level</Label>
            <Select value={activity} onValueChange={setActivity}>
              <SelectTrigger className="mt-2 h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1.2">Sedentary (1.2)</SelectItem>
                <SelectItem value="1.3">Light (1.3)</SelectItem>
                <SelectItem value="1.5">Moderate (1.5)</SelectItem>
                <SelectItem value="1.7">Heavy (1.7)</SelectItem>
                <SelectItem value="1.8">Very Heavy (1.8)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Calorie Deficit/Surplus */}
          <div className="bg-white rounded-lg border p-4">
            <Label className="text-xs font-medium">Calorie Adjustment</Label>
            <div className="mt-2 space-y-2">
              <Input
                type="number"
                value={deficitCalories}
                onChange={e => setDeficitCalories(e.target.value)}
                className="h-8 text-sm"
                placeholder="0"
              />
              <div className="text-xs text-gray-500">
                <div>• Positive (+500): Bulk/Gain weight</div>
                <div>• Negative (-500): Cut/Lose weight</div>
                <div>• Zero (0): Maintain weight</div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Diet Plan Details */}
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-medium mb-3 text-sm">Diet Plan Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="diet-plan-name" className="text-xs">Plan Name*</Label>
                <Input 
                  id="diet-plan-name" 
                  type="text" 
                  value={dietPlanName} 
                  onChange={e => setDietPlanName(e.target.value)} 
                  className="mt-1 h-8 text-sm" 
                  placeholder="e.g., Weight Loss Plan"
                />
              </div>
              <div>
                <Label htmlFor="diet-plan-date" className="text-xs">Date*</Label>
                <Input 
                  id="diet-plan-date" 
                  type="date" 
                  value={dietPlanDate} 
                  onChange={e => setDietPlanDate(e.target.value)} 
                  className="mt-1 h-8 text-sm"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={handleSave} disabled={!dietPlanName || !dietPlanDate || !bmr || !calories}>
                {editingData ? 'Update Diet Plan' : 'Save Diet Plan'}
              </Button>
            </div>
          </div>

          {/* Top Row: Calories + Macros */}
          <div className="flex flex-wrap gap-4 w-full">
            {/* Calories Box */}
            <div className="flex-1 bg-green-50 rounded-lg p-8 text-center flex flex-col justify-center min-w-[300px]">
              <div className="text-xs text-gray-600 mb-1">Total Daily Calories</div>
              <div className="text-4xl font-bold text-green-700 mb-1">
                {calories || '--'}
              </div>
              <div className="text-xs text-gray-500 mb-2">kcal/day</div>
              {bmr && activity && (
                <div className="text-xs text-gray-600">
                  {bmr} × {activity} = {baseCalories}
                  {Number(deficitCalories) !== 0 && (
                    <div className="text-xs text-gray-600">
                      {baseCalories} {Number(deficitCalories) > 0 ? '+' : ''}{deficitCalories} = {calories}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Macros Box */}
            <div className="flex-1 bg-white rounded-lg border p-6 flex flex-col justify-between min-w-[300px] min-h-[220px]">
              <div>
                <h3 className="font-medium mb-3 text-sm">Macronutrients</h3>
                <div className="space-y-3">
                  {/* Protein */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-medium text-purple-700">Protein</div>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={proteinPercent} onChange={e => setProteinPercent(e.target.value)} className="w-20 h-6 text-xs text-center font-bold border-2 border-purple-200 bg-purple-50" />
                      <span className="text-xs">%</span>
                      <div className="w-16 text-right text-sm font-bold text-purple-600">
                        {proteinGrams || '--'}g
                      </div>
                    </div>
                  </div>

                  {/* Carbs */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-medium text-yellow-700">Carbs</div>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={carbPercent} onChange={e => setCarbPercent(e.target.value)} className="w-20 h-6 text-xs text-center font-bold border-2 border-yellow-200 bg-yellow-50" />
                      <span className="text-xs">%</span>
                      <div className="w-16 text-right text-sm font-bold text-yellow-600">
                        {carbGrams || '--'}g
                      </div>
                    </div>
                  </div>

                  {/* Fat */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-medium text-pink-700">Fat</div>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={fatPercent} onChange={e => setFatPercent(e.target.value)} className="w-20 h-6 text-xs text-center font-bold border-2 border-pink-200 bg-pink-50" />
                      <span className="text-xs">%</span>
                      <div className="w-16 text-right text-sm font-bold text-pink-600">
                        {fatGrams || '--'}g
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 text-xs text-gray-400 text-center">
                Protein/Carbs: 4 cal/g | Fat: 9 cal/g
              </div>
            </div>
          </div>

          {/* Bottom Row: BMR Formula */}
          <div className="w-full bg-blue-50 rounded-xl border border-blue-100 shadow p-6 flex flex-col items-center">
            <div className="text-base font-bold mb-2">Adjustable BMR Formula</div>
            <div className="flex flex-wrap items-center justify-center gap-2 text-lg font-bold">
              <Input type="number" value={factor1} onChange={e => setFactor1(e.target.value)} className="w-24 h-8 text-center font-bold bg-white border-2 border-blue-200 rounded-lg" />
              <span className="mx-1">×</span>
              <span className="font-semibold text-blue-700">W</span>
              <span className="mx-1">+</span>
              <Input type="number" value={factor2} onChange={e => setFactor2(e.target.value)} className="w-24 h-8 text-center font-bold bg-white border-2 border-blue-200 rounded-lg" />
              <span className="mx-1">×</span>
              <span className="font-semibold text-blue-700">H</span>
              <span className="mx-1">-</span>
              <Input type="number" value={factor3} onChange={e => setFactor3(e.target.value)} className="w-24 h-8 text-center font-bold bg-white border-2 border-blue-200 rounded-lg" />
              <span className="mx-1">×</span>
              <span className="font-semibold text-blue-700">A</span>
              <span className="mx-1">+</span>
              <Input type="number" value={factor4} onChange={e => setFactor4(e.target.value)} className="w-24 h-8 text-center font-bold bg-white border-2 border-blue-200 rounded-lg" />
            </div>
            <div className="text-xs text-gray-500 mt-2">W = Weight, H = Height, A = Age</div>
            {calcError && (
              <div className="text-red-600 text-xs mt-2 text-center">{calcError}</div>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  if (equation === 'katch') {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Inputs & BMR */}
        <div className="space-y-4">
          {/* Basic Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium mb-3 text-sm">Basic Information</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="katch-weight" className="text-xs">Weight (kg)</Label>
                <Input 
                  id="katch-weight" 
                  type="number" 
                  value={weight} 
                  onChange={e => setWeight(e.target.value)} 
                  className="mt-1 h-8 text-sm" 
                  placeholder="70"
                />
              </div>
              <div>
                <Label htmlFor="katch-fat" className="text-xs">Body Fat %</Label>
                <Input 
                  id="katch-fat" 
                  type="number" 
                  value={fatBody} 
                  onChange={e => setFatBody(e.target.value)} 
                  className="mt-1 h-8 text-sm" 
                  placeholder="15"
                />
              </div>
            </div>
          </div>

          {/* BMR Result */}
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-xs text-gray-600 mb-1">BMR</div>
            <div className="text-3xl font-bold text-blue-600 mb-1">
              {bmr || '--'}
            </div>
            <div className="text-xs text-gray-500">cal/day</div>
          </div>

          {/* Activity Level */}
          <div className="bg-white rounded-lg border p-4">
            <Label className="text-xs font-medium">Activity Level</Label>
            <Select value={activity} onValueChange={setActivity}>
              <SelectTrigger className="mt-2 h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1.2">Sedentary (1.2)</SelectItem>
                <SelectItem value="1.3">Light (1.3)</SelectItem>
                <SelectItem value="1.5">Moderate (1.5)</SelectItem>
                <SelectItem value="1.7">Heavy (1.7)</SelectItem>
                <SelectItem value="1.8">Very Heavy (1.8)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Calorie Deficit/Surplus */}
          <div className="bg-white rounded-lg border p-4">
            <Label className="text-xs font-medium">Calorie Adjustment</Label>
            <div className="mt-2 space-y-2">
              <Input
                type="number"
                value={deficitCalories}
                onChange={e => setDeficitCalories(e.target.value)}
                className="h-8 text-sm"
                placeholder="0"
              />
              <div className="text-xs text-gray-500">
                <div>• Positive (+500): Bulk/Gain weight</div>
                <div>• Negative (-500): Cut/Lose weight</div>
                <div>• Zero (0): Maintain weight</div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Diet Plan Details */}
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-medium mb-3 text-sm">Diet Plan Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="diet-plan-name" className="text-xs">Plan Name*</Label>
                <Input 
                  id="diet-plan-name" 
                  type="text" 
                  value={dietPlanName} 
                  onChange={e => setDietPlanName(e.target.value)} 
                  className="mt-1 h-8 text-sm" 
                  placeholder="e.g., Weight Loss Plan"
                />
              </div>
              <div>
                <Label htmlFor="diet-plan-date" className="text-xs">Date*</Label>
                <Input 
                  id="diet-plan-date" 
                  type="date" 
                  value={dietPlanDate} 
                  onChange={e => setDietPlanDate(e.target.value)} 
                  className="mt-1 h-8 text-sm"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={handleSave} disabled={!dietPlanName || !dietPlanDate || !bmr || !calories}>
                {editingData ? 'Update Diet Plan' : 'Save Diet Plan'}
              </Button>
            </div>
          </div>

          {/* Top Row: Calories + Macros */}
          <div className="flex flex-wrap gap-4 w-full">
            {/* Calories Box */}
            <div className="flex-1 bg-green-50 rounded-lg p-8 text-center flex flex-col justify-center min-w-[300px]">
              <div className="text-xs text-gray-600 mb-1">Total Daily Calories</div>
              <div className="text-4xl font-bold text-green-700 mb-1">
                {calories || '--'}
              </div>
              <div className="text-xs text-gray-500 mb-2">kcal/day</div>
              {bmr && activity && (
                <div className="text-xs text-gray-600">
                  {bmr} × {activity} = {baseCalories}
                  {Number(deficitCalories) !== 0 && (
                    <div className="text-xs text-gray-600">
                      {baseCalories} {Number(deficitCalories) > 0 ? '+' : ''}{deficitCalories} = {calories}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Macros Box */}
            <div className="flex-1 bg-white rounded-lg border p-6 flex flex-col justify-between min-w-[300px] min-h-[220px]">
              <div>
                <h3 className="font-medium mb-3 text-sm">Macronutrients</h3>
                <div className="space-y-3">
                  {/* Protein */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-medium text-purple-700">Protein</div>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={proteinPercent} onChange={e => setProteinPercent(e.target.value)} className="w-20 h-6 text-xs text-center font-bold border-2 border-purple-200 bg-purple-50" />
                      <span className="text-xs">%</span>
                      <div className="w-16 text-right text-sm font-bold text-purple-600">
                        {proteinGrams || '--'}g
                      </div>
                    </div>
                  </div>

                  {/* Carbs */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-medium text-yellow-700">Carbs</div>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={carbPercent} onChange={e => setCarbPercent(e.target.value)} className="w-20 h-6 text-xs text-center font-bold border-2 border-yellow-200 bg-yellow-50" />
                      <span className="text-xs">%</span>
                      <div className="w-16 text-right text-sm font-bold text-yellow-600">
                        {carbGrams || '--'}g
                      </div>
                    </div>
                  </div>

                  {/* Fat */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-medium text-pink-700">Fat</div>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={fatPercent} onChange={e => setFatPercent(e.target.value)} className="w-20 h-6 text-xs text-center font-bold border-2 border-pink-200 bg-pink-50" />
                      <span className="text-xs">%</span>
                      <div className="w-16 text-right text-sm font-bold text-pink-600">
                        {fatGrams || '--'}g
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 text-xs text-gray-400 text-center">
                Protein/Carbs: 4 cal/g | Fat: 9 cal/g
              </div>
            </div>
          </div>

          {/* Bottom Row: BMR Formula */}
          <div className="w-full bg-blue-50 rounded-xl border border-blue-100 shadow p-6 flex flex-col items-center">
            <div className="text-base font-bold mb-2">Adjustable BMR Formula</div>
            <div className="flex flex-wrap items-center justify-center gap-2 text-lg font-bold">
              <Input type="number" value={factor1} onChange={e => setFactor1(e.target.value)} className="w-24 h-8 text-center font-bold bg-white border-2 border-blue-200 rounded-lg" />
              <span className="mx-1">+</span>
              <Input type="number" value={factor2} onChange={e => setFactor2(e.target.value)} className="w-24 h-8 text-center font-bold bg-white border-2 border-blue-200 rounded-lg" />
              <span className="mx-1">×</span>
              <span className="font-semibold text-blue-700">(1 - </span>
              <Input type="number" value={fatBody} onChange={e => setFatBody(e.target.value)} className="w-20 h-8 text-center font-bold bg-white border-2 border-blue-200 rounded-lg" />
              <span className="font-semibold text-blue-700">% ) × W</span>
            </div>
            <div className="text-xs text-gray-500 mt-2">W = Weight, Body Fat % = Fat Percentage</div>
            {calcError && (
              <div className="text-red-600 text-xs mt-2 text-center">{calcError}</div>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Placeholder for other equations
  return (
    <div className="p-8 text-center text-muted-foreground">
      <div className="text-lg font-medium mb-2">Coming Soon</div>
      <div className="text-sm">This calculation method will be available in a future update.</div>
    </div>
  );
}